import { toPairs, fromPairs, compose, map, filter } from 'ramda'
import { fromNullable } from 'data.maybe'
import querystring from 'querystring'
import { post, get, createFetchTask } from '@integec/fetch-task'
import { eventTypes as eventTypesMap } from 'card-data/utils'
import changeCase from 'change-case'
import moment from 'moment'
import { getEffectiveJwt } from 'utils'
import { DATE_FORMAT_API } from 'enum'

const normalizeValue = value =>
  fromNullable(value)
    .map(v => (moment.isMoment(v) ? v.format() : v))
    .getOrElse(null)

export const toQueryStr = compose(
  querystring.stringify,
  fromPairs,
  map(([key, value]) => [changeCase.snakeCase(key), normalizeValue(value)]),
  filter(([_key, value]) => value != null && value !== ''),
  toPairs
)

export const getTest = targetInfo => () => get(targetInfo, 'test')

export const getAppInitRequiredData = targetInfo =>
  get(targetInfo, 'init/init-loads-required')

export const getAppInitData = targetInfo =>
  get(targetInfo, 'init/initialization')

export const logoutCall = targetInfo => post(targetInfo, 'admin/logout')()

export const getUnitsOnDates = targetInfo => (
  moment,
  endDate,
  withRetriedUnits = 'N'
) => {
  return get(
    targetInfo,
    `init/init-units?${querystring.stringify({
      date: moment
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD'),
      endDate: endDate.isBefore(moment)
        ? moment
            .clone()
            .endOf('month')
            .format('YYYY-MM-DD')
        : endDate
            .clone()
            .endOf('month')
            .format('YYYY-MM-DD'),
      withRetriedUnits,
    })}`
  )
}

export const getUnits = targetInfo => (moment, endDate) =>
  get(
    targetInfo,
    `unit?${querystring.stringify({
      date: moment
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD'),
      endDate: endDate.isBefore(moment)
        ? moment
            .clone()
            .endOf('month')
            .format('YYYY-MM-DD')
        : endDate
            .clone()
            .endOf('month')
            .format('YYYY-MM-DD'),
    })}`
  )

export const getAllUnitsByUser = targetInfo => {
  // this is when client required to see units without checking retired and commission date
  return get(targetInfo, 'unit/allunits-byuser')
}

export const getHeInDay = targetInfo => moment =>
  get(targetInfo, `utils/pjm-he-in-day?date=${moment.format('YYYY-MM-DD')}`)

export const getUtilityNameMap = targetInfo => _ =>
  get(targetInfo, 'utils/utility-name-map')
export const getUnitNameMap = targetInfo =>
  get(targetInfo, 'unit/unit-name-map')
export const getUtilityUnitMap = targetInfo => date =>
  get(targetInfo, `utils/utility-unit-map?date=${date.format('YYYY-MM-DD')}`)

export const getUnitSystemCompCauseCodeMap = targetInfo =>
  get(targetInfo, 'utils/unitcode-causecode')

export const findSubmissionLogs = targetInfo => request =>
  post(targetInfo, 'utils/submit-trans-logs')(request)

export const getEnvConfig = targetInfo => _ =>
  get(targetInfo, 'utils/env-config')

export const getMSAuthConfig = targetInfo =>
  get(targetInfo, 'common/ms-auth-configuration')

export const getUserInfo = targetInfo => get(targetInfo, 'user/user-info')

export const saveUserInfo = targetInfo => data =>
  post(targetInfo, 'user/user-info')(data)

export const getUnitInfo = targetInfo => get(targetInfo, 'unit')
export const getAllUnitInfo = targetInfo => get(targetInfo, 'unit/unitall')

export const postUnitInfo = targetInfo => (data, isNew) =>
  post(targetInfo, `unit?isNew=${isNew}`)(data)

export const deleteUnitInfo = targetInfo => data =>
  post(targetInfo, 'unit/del')(data)

export const getRuleSetInfo = targetInfo => get(targetInfo, 'utils/rule-set')

/* company */
export const getCompanyInfo = targetInfo => get(targetInfo, 'admin/company')

export const postCompanyInfo = targetInfo => data =>
  post(targetInfo, 'admin/company')(data)

export const deleteCompanyInfo = targetInfo => data =>
  post(targetInfo, 'admin/company/del')(data)

/* System Configuration */
export const getSystemConfigurationInfo = targetInfo =>
  get(targetInfo, 'admin/system-configuration')

export const postSystemConfigurationInfo = targetInfo => data =>
  post(targetInfo, 'admin/system-configuration')(data)

export const deleteSystemConfigurationInfo = targetInfo => data =>
  post(targetInfo, 'admin/system-configuration/del')(data)

/* Admin - Process Log */

export const getProcessLogInfo = targetInfo => ({ startDate, endDate }) =>
  get(
    targetInfo,
    `admin/process-log?${querystring.stringify({
      // transdate: moment.format('YYYY-MM-DD'),
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )

export const getCompanyNameMap = targetInfo => _ =>
  get(targetInfo, 'utils/company-name-map')

/* admin => Unit Group */
export const getUnitGroup = targetInfo => (
  moment,
  orig = true,
  isAll = false
) => {
  const date = orig ? moment : moment.clone().endOf('month')
  return get(
    targetInfo,
    `admin/unit-group?${querystring.stringify({
      all: isAll,
      effectivedate: date
        // .clone()
        // .endOf('month')
        .format('YYYY-MM-DD'),
    })}`
  )
}

export const getUnitGroupScreen = targetInfo => ({
  moment,
  orig = true,
  isAll = false,
}) => getUnitGroup(targetInfo)(moment, orig, isAll)
export const postUnitGroup = targetInfo => data =>
  post(targetInfo, 'admin/unit-group')([data])

export const deleteUnitGroup = targetInfo => data =>
  post(targetInfo, 'admin/unit-group/del')([data])

export const getUnitGroupAll = targetInfo => (moment, orig = true) => {
  const date = orig ? moment : moment.clone().endOf('month')
  return get(
    targetInfo,
    `admin/unit-group-all?${querystring.stringify({
      effectivedate: date
        // .clone()
        // .endOf('month')
        .format('YYYY-MM-DD'),
    })}`
  )
}

/* admin => eSuite Management */
export const getESuiteMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/esuite-management')

export const postESuiteMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/esuite-management')([data])

export const deleteESuiteMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/esuite-management/del')(data)

/* admin => Unit Type */
export const getUnitTypeInfo = targetInfo => get(targetInfo, 'admin/unit-type')

export const postUnitTypeInfo = targetInfo => data =>
  post(targetInfo, 'admin/unit-type')(data)

export const deleteUnitTypeInfo = targetInfo => data =>
  post(targetInfo, 'admin/unit-type/del')(data)

/* admin => Utility Operator */
export const getUtilityOperatorInfo = targetInfo =>
  get(targetInfo, 'admin/utility-operator')

export const postUtilityOperatorInfo = targetInfo => data =>
  post(targetInfo, 'admin/utility-operator')(data)

export const deleteUtilityOperatorInfo = targetInfo => data =>
  post(targetInfo, 'admin/utility-operator/del')(data)

/* admin => Fuel Code */
export const getFuelCodeInfo = targetInfo => get(targetInfo, 'admin/fuel-code')

export const postFuelCodeInfo = targetInfo => data =>
  post(targetInfo, 'admin/fuel-code')(data)

export const deleteFuelCodeInfo = targetInfo => data =>
  post(targetInfo, 'admin/fuel-code/del')(data)

// Station
export const getStationInfo = targetInfo => get(targetInfo, 'admin/station')

export const postStationInfo = targetInfo => data =>
  post(targetInfo, 'admin/station')(data)

export const deleteStationInfo = targetInfo => data =>
  post(targetInfo, 'admin/station/del')(data)

// TPS unit Mapping
export const getTpsUnitMappingInfo = targetInfo =>
  get(targetInfo, 'cleco/tps-unit-mapping')

export const postTpsUnitMappingInfo = targetInfo => data =>
  post(targetInfo, 'cleco/tps-unit-mapping')(data)

export const deleteTpsUnitMappingInfo = targetInfo => data =>
  post(targetInfo, 'cleco/tps-unit-mapping/del')(data)

// MISO unit Mapping
export const getMisoUnitMappingInfo = targetInfo =>
  get(targetInfo, 'cleco/miso-unit-mapping')

export const postMisoUnitMappingInfo = targetInfo => data =>
  post(targetInfo, 'cleco/miso-unit-mapping')(data)

export const deleteMisoUnitMappingInfo = targetInfo => data =>
  post(targetInfo, 'cleco/miso-unit-mapping/del')(data)

// PPR
export const getPpr = targetInfo => (startDate, endDate, utilityUnitList) =>
  post(
    targetInfo,
    `cleco/ppr?${querystring.stringify({
      startdate: startDate.format('YYYY-MM-DD'),
      enddate: endDate.format('YYYY-MM-DD'),
    })}`
  )(utilityUnitList)

export const getTpsMaDataImport = targetInfo => (
  startDate,
  endDate,
  elementNames
) =>
  post(
    targetInfo,
    `cleco/tps-ma-data-import/retrieve?${querystring.stringify({
      startdate: startDate.format('YYYY-MM-DD'),
      enddate: endDate.format('YYYY-MM-DD'),
    })}`
  )(elementNames)

export const importTpsMaDataImport = targetInfo => (
  startDate,
  endDate,
  elementNames
) =>
  post(
    targetInfo,
    `cleco/tps-ma-data-import/import?${querystring.stringify({
      startdate: startDate.format('YYYY-MM-DD'),
      enddate: endDate.format('YYYY-MM-DD'),
    })}`
  )(elementNames)

export const getMisoMaDataImport = targetInfo => (date, elementNames) =>
  post(
    targetInfo,
    `cleco/miso-ma-data-import/retrieve?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )(elementNames)

export const importMisoMaDataImport = targetInfo => date =>
  post(
    targetInfo,
    `cleco/miso-ma-data-import/import?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )()

/* admin => Pnode and Emkt Id Management */
export const getPnodeAndEmktMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/pnode-emktid-management')

export const postPnodeAndEmktMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/pnode-emktid-management')(data.map(d => ({ ...d })))

export const deletePnodeAndEmktMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/pnode-emktid-management/del')(data)

/* admin => Amplification Code Management */
export const getAmpCodeMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/amp-code')

export const postAmpCodeMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/amp-code')(data)

export const deleteAmpCodeMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/amp-code/del')(data)

/* admin => Cause Code Management */
export const getCauseCodeMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/cause-code')

export const postCauseCodeMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/cause-code')(data)

export const deleteCauseCodeMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/cause-code/del')(data)

/* admin => System/Component Management */
export const getSystemCompMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/system-comp-mgmt')

export const postSystemCompMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/system-comp-mgmt')(data)

export const deleteSystemCompMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/system-comp-mgmt/del')(data)

export const getUnitCodeSystemCompMap = targetInfo =>
  get(targetInfo, 'utils/unitcode-system-comp-map')

/* admin => GADS Error Management */
export const getGadsErrorMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/gads-error')

export const postGadsErrorMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/gads-error')(data)

export const deleteGadsErrorMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/gads-error/del')(data)

/* admin => Fail Code Management */
export const getFailCodeMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/fail-code-mgmt')

export const postFailCodeMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/fail-code-mgmt')(data)

export const deleteFailCodeMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/fail-code-mgmt/del')(data)

/* admin => NERC Capacity */
export const getNERCCapacityInfo = targetInfo =>
  get(targetInfo, 'admin/nerc-capacity')

export const postNERCCapacityInfo = targetInfo => data =>
  post(targetInfo, 'admin/nerc-capacity')(data)

export const deleteNERCCapacityInfo = targetInfo => data =>
  post(targetInfo, 'admin/nerc-capacity/del')(data)

/* admin => GADS Holiday Management */
export const getHolidayMgmtInfo = targetInfo =>
  get(targetInfo, 'admin/holiday-mgmt')

export const postHolidayMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/holiday-mgmt')(data)

export const deleteHolidayMgmtInfo = targetInfo => data =>
  post(targetInfo, 'admin/holiday-mgmt/del')(data)

/* admin => Percent Ownership */
export const getPercentOwnershipInfo = targetInfo =>
  get(targetInfo, 'admin/percent-ownership')

export const postPercentOwnershipInfo = targetInfo => data =>
  post(targetInfo, 'admin/percent-ownership')(data)

export const deletePercentOwnershipInfo = targetInfo => data =>
  post(targetInfo, 'admin/percent-ownership/del')(data)

export const getUnitExtension = targetInfo =>
  get(targetInfo, 'admin/unit-extension')

export const postUnitExtension = targetInfo => data =>
  post(targetInfo, 'admin/unit-extension')(data)

export const deleteUnitExtension = targetInfo => data =>
  post(targetInfo, 'admin/unit-extension/delete')(data)

/* admin => Universal ID */
export const getUniversalIdInfo = targetInfo =>
  get(targetInfo, 'admin/universalid')

export const getNercClassAvgEford = targetInfo =>
  get(targetInfo, 'admin/nerc-class-avg')
export const deleteNercClassAvgEford = targetInfo => data =>
  post(targetInfo, 'admin/nerc-class-avg/delete')(data)
export const saveNercClassAvgEford = targetInfo => data =>
  post(targetInfo, 'admin/nerc-class-avg')(data)

export const getNEUSers = targetInfo => get(targetInfo, 'admin/ne-users')
export const getNEUserMapping = targetInfo =>
  get(targetInfo, 'admin/ne-users-mapping')

export const getUnitBlock = targetInfo => get(targetInfo, 'admin/unit-block')
export const updateUnitBlock = targetInfo => data =>
  post(targetInfo, 'admin/unit-block')(data)
export const deleteUnitBlock = targetInfo => data =>
  post(targetInfo, 'admin/unit-block/delete')(data)

export const getBfNyIsoDerateGroup = targetInfo =>
  get(targetInfo, 'admin/bfnyisoderategroup')

export const deleteBfNyIsoDerateGroup = targetInfo => data => {
  const { utility, unit } = data[0]
  return post(
    targetInfo,
    `admin/bfnyisoderategroup/delete?${querystring.stringify({
      utility,
      unit: unit,
    })}`
  )()
}
export const insertBfNyIsoDerateGroup = targetInfo => data => {
  const { utility, unit } = data[0]
  return post(
    targetInfo,
    `admin/bfnyisoderategroup?${querystring.stringify({
      utility,
      unit: unit,
    })}`
  )()
}

/* admin => Global Unit Re-Assignment */
export const globalUnitReAssignmentInfo = targetInfo => (
  oldUtility,
  oldUnit,
  newUtility,
  newUnit
) =>
  get(
    targetInfo,
    `admin/global-unit-reassignment?${querystring.stringify({
      'old-utility': oldUtility == null ? '' : oldUtility,
      'old-unit': oldUnit == null ? '' : oldUnit,
      'new-utility': newUtility == null ? '' : newUtility,
      'new-unit': newUnit == null ? '' : newUnit,
    })}`
  )

// export const getReportNames = targetInfo => get(targetInfo, 'report/reportname')
export const getReportInitLoad = targetInfo =>
  get(targetInfo, 'init/report-init-load')

export const postReport = targetInfo => data =>
  post(targetInfo, 'report/loadreport')(data)

export const postJasperReport = targetInfo => data =>
  post(targetInfo, 'report/jasperreport-loadreport')(data)

export const getJasperReports = targetInfo =>
  get(targetInfo, 'report/jasper-reports')

export const getDataItems = targetInfo => get(targetInfo, 'admin/data-items')
export const getAdhocRptDataGroups = targetInfo =>
  get(targetInfo, 'adhoc-report/data-groups')
export const postAdhocRptDataGroup = targetInfo => data =>
  post(targetInfo, 'adhoc-report/save-data-group')(data)
export const getAdhocRptUnitGroups = targetInfo => date =>
  get(targetInfo, `adhoc-report/unit-groups?date=${date}`)
export const postAdhocRptUnitGroup = targetInfo => data =>
  post(targetInfo, 'adhoc-report/save-unit-group')(data)
export const getAdhocCausecodeGroups = targetInfo =>
  get(targetInfo, 'adhoc-report/causecode-groups')
export const postAdhocRptCausecodeGroup = targetInfo => data =>
  post(targetInfo, 'adhoc-report/save-causecode-group')(data)
export const generateReport = targetInfo => data =>
  post(targetInfo, 'adhoc-report/generate-report')(data)

export const runAdhocReportInBackground = targetInfo => data =>
  post(targetInfo, 'adhoc-report/generateInBackground')(data)

export const exportAdhocReportPdf = targetInfo => data => {
  const URL = `${targetInfo.baseUrl}/adhoc-report/export-pdf`
  return fetch(`${URL}`, {
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      ...getAuthorization(targetInfo),
    },
    body: JSON.stringify(data),
  }).then(response => {
    return response.blob()
  })
}

export const exportAdhocReportXlsx = targetInfo => data => {
  const URL = `${targetInfo.baseUrl}/adhoc-report/export-excel`
  return fetch(`${URL}`, {
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      ...getAuthorization(targetInfo),
    },
    body: JSON.stringify(data),
  }).then(response => response.blob())
}

export const getReportMessages = targetInfo => data =>
  post(targetInfo, 'adhoc-report/report-messages')(data)
export const getAdhocData = targetInfo => data =>
  post(targetInfo, 'adhoc-report/adhoc-data')(data)

export const getCauseCodeAll = targetInfo =>
  get(targetInfo, 'admin/causecode-group-all')

export const getEventTypes = targetInfo => get(targetInfo, 'admin/eventypes')
export const postEventTypes = targetInfo => data =>
  post(targetInfo, 'admin/eventypes')(data)
export const postEventTypesDelete = targetInfo => data =>
  post(targetInfo, 'admin/eventypes/delete')(data)
// export const getStationName = targetInfo => get(targetInfo, 'utils/station-name')

// GADS Card Data - Card95
export const getGrossNoGross = targetInfo =>
  get(targetInfo, 'cards/gross-nogross')
export const getCard95 = targetInfo => (
  year,
  month,
  utility,
  units,
  endYear,
  endMonth,
  isInsert = false
) =>
  post(
    targetInfo,
    `cards/card95/getdata?${querystring.stringify({
      year: year,
      month: month,
      utility: utility,
      //   units: units === undefined || units.length <= 0 ? '' : units,
      endYear,
      endMonth,
      isInsert,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)

export const postCard95 = (targetInfo, grossNoGross) => data =>
  post(
    targetInfo,
    `cards/card95?${querystring.stringify({
      grossNoGross,
    })}`
  )(data)

export const getCard95CheckRecord = targetInfo => (
  year,
  month,
  utility,
  units
) =>
  get(
    targetInfo,
    `cards/card95-checkrecord?${querystring.stringify({
      year,
      month,
      utility,
      units: units === undefined || units.length > 0 ? units[0] : '',
    })}`
  )

export const deleteCard95 = targetInfo => data =>
  post(targetInfo, 'cards/card95/del')(data)

// GADS Card Data - Card99
export const getCard99Extend = targetInfo =>
  get(targetInfo, 'cards/card99-extend')
export const getCard99 = targetInfo => (
  year,
  month,
  utility,
  units,
  endYear,
  endMonth
) =>
  post(
    targetInfo,
    `cards/card99/getdata?${querystring.stringify({
      year,
      month,
      utility,
      // units: units === undefined || units.length <= 0 ? '' : units,
      endYear: endYear || year,
      endMonth: endMonth || month,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)

export const getCard99CheckRecord = targetInfo => (
  year,
  month,
  utility,
  units
) =>
  get(
    targetInfo,
    `cards/card99-checkrecord?${querystring.stringify({
      year,
      month,
      utility,
      units: units === undefined || units.length > 0 ? units[0] : '',
    })}`
  )

export const postCard99 = (targetInfo, card99Extend) => data =>
  post(
    targetInfo,
    `cards/card99?${querystring.stringify({
      card99Extend,
    })}`
  )(data)

export const deleteCard99 = (targetInfo, card99Extend) => data =>
  post(
    targetInfo,
    `cards/card99/del?${querystring.stringify({
      card99Extend,
    })}`
  )(data)

// GADS Card Data - Card97
export const getCard97 = targetInfo => (year, utility, units, eventTypes) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return get(
    targetInfo,
    `cards/card97?${querystring.stringify({
      year: year,
      utility,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventTypes,
    })}`
  )
}

export const getCard97PJM = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `cards/card97/pjmdata?${querystring.stringify({
      year: year,
      utility,
      eventTypes,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)
}

export const getCard97ByPost = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `cards/card97/bypost?${querystring.stringify({
      year: year,
      utility,
      eventTypes,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)
}

export const getNRGCard97ByPost = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `nrg/card97/bypost?${querystring.stringify({
      year: year,
      utility,
      eventTypes,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)
}

export const getCard97NextEventNumber = targetInfo => ({
  year,
  utility,
  unit,
}) => {
  return get(
    targetInfo,
    `cards/card97/next-event-number?${querystring.stringify({
      year,
      utility,
      unit,
    })}`
  )
}

export const dukefossilCard97 = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return get(
    targetInfo,
    `cards/duke-fossil-ref-card97?${querystring.stringify({
      year,
      utility,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventTypes,
    })}`
  )
}

export const getCustomCard97Data = targetInfo => ({ year, units }) =>
  get(
    targetInfo,
    `common/custom-card97-data?${querystring.stringify({
      year,
      unitId: units === undefined || units.length <= 0 ? '' : units,
    })}`
  )

export const getDartReconciliation = targetInfo => ({ units }) =>
  get(
    targetInfo,
    `common/pdart-edart-outage?${querystring.stringify({
      unitId: units === undefined || units.length <= 0 ? '' : units,
    })}`
  )

export const submitDartReconciliation = targetInfo => data =>
  post(targetInfo, 'common/pdart-edart-outage/submit')(data)

export const ignoreDartReconciliation = targetInfo => data =>
  post(targetInfo, 'common/pdart-edart-outage/ignore')(data)

export const getCard97ExtensionSpec = targetInfo =>
  get(targetInfo, 'cards/card97-ext-scec')

export const getCard97ExtensionData = targetInfo => data =>
  get(targetInfo, `cards/card97-ext-data?${querystring.stringify(data)}`)

export const getCard97DataCheck = targetInfo => ({ year, units, eventnum }) =>
  get(
    targetInfo,
    `cards/card97-datacheck?${querystring.stringify({
      year,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventnum,
    })}`
  )

export const getCard97Idx = targetInfo => (year, utility, unit, eventnum) =>
  get(
    targetInfo,
    `cards/card97-idx?${querystring.stringify({
      year,
      utility,
      unit,
      eventnum,
    })}`
  )

export const postCard97 = targetInfo => data =>
  post(targetInfo, 'cards/card97')(data)

export const deleteCard97 = targetInfo => data =>
  post(targetInfo, 'cards/card97/del')(data)

export const saveCard97QuickFillMultiple = targetInfo => data =>
  post(targetInfo, 'card97-quick-fill-multiple')(data)

export const getCardHistoryAll = targetInfo => ({
  fromMonth,
  fromYear,
  toMonth,
  toYear,
  allOrLrRadio,
}) => units =>
  post(
    targetInfo,
    `card-history/card-history-data?${querystring.stringify({
      // utility: utility == undefined || utility.length <= 0 ? '' : utility,
      fromYear: fromYear == null ? '' : fromYear,
      fromMonth: fromMonth == null ? '' : fromMonth,
      toYear: toYear == null ? '' : toYear,
      toMonth: toMonth == null ? '' : toMonth,
      type: allOrLrRadio == null ? '' : allOrLrRadio,
    })}`
  )(units)

// nerc-file..

export const getNercErrors = targetInfo => data =>
  post(targetInfo, 'nerc-file/errors')(data)

export const exportData = (blob, type, fileName) => {
  const a = window.document.createElement('a')
  a.href = window.URL.createObjectURL(blob, { type })
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const nercExportFileNew = targetInfo => data =>
  post(targetInfo, 'nerc-file/export')(data)

export const nercExportFile = targetInfo => data => otherFunc => {
  const URL = `${targetInfo.baseUrl}/nerc-file/export`
  return fetch(`${URL}`, {
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      ...getAuthorization(targetInfo),
    },
    body: JSON.stringify(data),
  })
    .then(response => response.blob())
    .then(blob => exportData(blob, 'text/plain', 'nerc-05-07.txt'))
    .then(e => otherFunc && otherFunc(e))
}

// pjm file

export const getPJMErrors = targetInfo => data =>
  post(targetInfo, 'pjm-file/errors')(data)

export const getAuthorization = targetInfo => {
  if (targetInfo.jwt === undefined || targetInfo.jwt == null) {
    return {}
  } else {
    return { Authorization: `JWT ${getEffectiveJwt()}` }
  }
}

export const getCredentials = targetInfo =>
  targetInfo.fetchCredentials == null
    ? {}
    : { credentials: targetInfo.fetchCredentials }

export const pjmExportFile = targetInfo => data => otherFunc => {
  const URL = `${targetInfo.baseUrl}/pjm-file/export`
  return fetch(`${URL}`, {
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      ...getAuthorization(targetInfo),
    },
    body: JSON.stringify(data),
  })
    .then(response => response.blob())
    .then(blob => exportData(blob, 'text/plain', 'pjm-05-07.txt'))
    .then(b => otherFunc && otherFunc(b))
}

// data-import

export const importFile = targetInfo => file => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('file-type', file && file.type)
  return createFetchTask({
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: getAuthorization(targetInfo),
  })(
    targetInfo,
    'file-import/import'
  )(formData)
}

// Gads validation

export const getLevel2ValidationData = targetInfo => (
  year,
  month,
  utility,
  units
) =>
  post(
    targetInfo,
    `gads-validation/level-2-validation?${querystring.stringify({
      month: month == null ? '' : month,
      year: year == null ? '' : year,
      utility: utility == null ? '' : utility,
    })}`
  )(units === undefined || units.length <= 0 ? [] : units)

export const getLevel2ValidationErrorLog = targetInfo => (
  year,
  month,
  utility,
  units
) =>
  post(
    targetInfo,
    `gads-validation/level-2-validation-error-log?${querystring.stringify({
      month: month == null ? '' : month,
      year: year == null ? '' : year,
      utility: utility == null ? '' : utility,
      // units: units === undefined || units.length <= 0 ? '' : units,
    })}`
  )(units)

export const getLevel2ValidationStatus = targetInfo => (
  fromYear,
  fromMonth,
  toMonth,
  toYear,
  utility,
  units
) =>
  get(
    targetInfo,
    `gads-validation/level-2-validation-status?${querystring.stringify({
      fromYear: fromYear == null ? '' : fromYear,
      fromMonth: fromMonth == null ? '' : fromMonth,
      toMonth: toMonth == null ? '' : toMonth,
      toYear: toYear == null ? '' : toYear,
      utility: utility == null ? '' : utility,
      units: units == null ? '' : units,
    })}`
  )
export const getLevel2ValidationStatusByPost = targetInfo => (
  fromYear,
  fromMonth,
  toMonth,
  toYear,
  utility,
  units
) =>
  post(
    targetInfo,
    `gads-validation/level-2-validation-status-bypost?${querystring.stringify({
      fromYear: fromYear == null ? '' : fromYear,
      fromMonth: fromMonth == null ? '' : fromMonth,
      toMonth: toMonth == null ? '' : toMonth,
      toYear: toYear == null ? '' : toYear,
      utility: utility == null ? '' : utility,
    })}`
  )(units)

export const getPjmLevel2ValidationStatus = targetInfo => (
  fromYear,
  fromMonth,
  toMonth,
  toYear,
  utility,
  units
) =>
  post(
    targetInfo,
    `gads-validation/pjm-level-2-validation-status?${querystring.stringify({
      fromYear: fromYear == null ? '' : fromYear,
      fromMonth: fromMonth == null ? '' : fromMonth,
      toMonth: toMonth == null ? '' : toMonth,
      toYear: toYear == null ? '' : toYear,
    })}`
  )(units)

export const getLevel2ValidationTab = targetInfo => (
  year,
  month,
  utility,
  units,
  runcheck,
  dataArea
) =>
  post(
    targetInfo,
    `gads-validation/level-2-validation-tab?${querystring.stringify({
      month: month == null ? '' : month,
      year: year == null ? '' : year,
      utility: utility == null ? '' : utility,
      // units: units === undefined || units.length <= 0 ? '' : units,
      runcheck,
      dataArea,
    })}`
  )(units === undefined || units.length <= 0 ? [] : units)

// all data for screen data-import in one api
export const allImportData = targetInfo => file => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('file-type', file && file.type)
  return createFetchTask({
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: getAuthorization(targetInfo),
  })(
    targetInfo,
    'file-import/import-edit'
  )(formData)
}

export const resubmitData = targetInfo => (data, fileType) =>
  post(
    targetInfo,
    `file-import/resubmit-file?${querystring.stringify({ fileType })}`
  )(data)

export const getMonthlyViewData = targetInfo => (
  year,
  month,
  utility,
  unit,
  shortName
) =>
  get(
    targetInfo,
    `monthlyview/monthlydata?${querystring.stringify({
      utility,
      unit,
      shortName,
      year,
      month,
    })}`
  )

// save perf data from monthly perf screen
export const savePerfData = (
  targetInfo,
  data,
  year,
  month,
  utility,
  unit,
  shortName
) => () =>
  post(
    targetInfo,
    `monthlyview/save_perf_data?${querystring.stringify({
      year,
      month,
      utility,
      unit,
      shortName,
    })}`
  )(data)

export const saveDailyPerfData = (
  targetInfo,
  year,
  month,
  utility,
  unit,
  shortName,
  data
) => () =>
  post(
    targetInfo,
    `monthlyview/save_daily_perf_data?${querystring.stringify({
      year,
      month,
      utility,
      unit,
      shortName,
    })}`
  )(data)

export const getNERCCapacityInfoOnUnit = targetInfo => unit =>
  get(
    targetInfo,
    `admin/nerc-capacity-unit?${querystring.stringify({
      unit: unit.map(u => u.utility + '' + u.unit),
    })}`
  )

export const doPasswordReset = targetInfo => request =>
  post(targetInfo, 'auth/token/reset', { ignoreAuth: true })(request)

export const updateUsers = targetInfo => data => post(targetInfo, 'user')(data)

export const getUsers = targetInfo => () => get(targetInfo, 'user')

export const deleteUsers = targetInfo => data =>
  post(targetInfo, 'user/del')([data])

export const getKpi = targetInfo => ({ utility, unit, year, month }) =>
  get(
    targetInfo,
    `monthlyview/kpi?${querystring.stringify({
      utility,
      unit: unit.substr(utility.length),
      year,
      month,
    })}`
  )

export const saveKpi = targetInfo => (setting, template) =>
  post(targetInfo, 'monthlyview/kpi')({ setting, template })

export const getAllActions = targetInfo => get(targetInfo, 'gads-auth/actions')
export const getUserActions = targetInfo =>
  get(targetInfo, 'gads-auth/user-actions')

export const saveRole = targetInfo => data =>
  post(targetInfo, 'gads-auth/role')(data)

export const deleteRole = targetInfo => data =>
  post(targetInfo, `gads-auth/role/delete?${toQueryStr(data)}`)({})

export const getRoles = targetInfo => () => get(targetInfo, 'gads-auth/roles')
export const getGorpIrmb = targetInfo => get(targetInfo, 'report/gorp-irmb')
export const getReportMaGroups = targetInfo =>
  get(targetInfo, 'report/ma-group')
export const getReportWindSelection = targetInfo =>
  get(targetInfo, 'report/wind-selection')
export const getReportNameMap = targetInfo =>
  get(targetInfo, 'admin/report-name-map-list')

export const getUserReports = targetInfo =>
  get(targetInfo, 'gads-auth/user-reports')

export const getUserRoles = targetInfo =>
  get(targetInfo, 'gads-auth/frontend-roles')

export const card97DescWizard = targetInfo => data =>
  get(targetInfo, `cards/card97-descwizard?${querystring.stringify(data)}`)

export const getMonthlyLock = targetInfo => get(targetInfo, 'admin/monthlylock')

export const updateMonthlyLock = targetInfo => data =>
  post(targetInfo, 'admin/monthlylock')(data)

export const deleteMonthlyLock = targetInfo => data =>
  post(targetInfo, 'admin/monthlylock/del')(data)

export const getUnitLockCheckRelease = targetInfo =>
  get(targetInfo, 'admin/lock-check')

export const deleteUnitLockRelease = targetInfo => data =>
  post(targetInfo, 'admin/lock-check/del')(data)

export const postUnitLockRelease = targetInfo => data =>
  post(targetInfo, 'admin/lock-check')(data)

export const nonHydroProcess = targetInfo => ({ units, ...props }) =>
  post(targetInfo, `nonhydro/process?${querystring.stringify(props)}`)(units)

export const getNonHydroEvents = targetInfo => ({ unitIds, ...props }) =>
  post(targetInfo, `nonhydro/events?${querystring.stringify(props)}`)(unitIds)

export const getLastNonHydroEvent = targetInfo => data =>
  get(targetInfo, `nonhydro/last-event?${querystring.stringify(data)}`)

export const saveNonHydroEvents = targetInfo => data =>
  post(targetInfo, 'nonhydro/events-save')(data)

export const nonHydroUnitLockStatus = targetInfo => ({ units, ...props }) =>
  post(
    targetInfo,
    `nonhydro/nonhydro-unit-lock-status?${querystring.stringify(props)}`
  )(units)

export const hydroUnitLockStatus = targetInfo => ({ units, ...props }) =>
  post(
    targetInfo,
    `hydro/hydro-unit-lock-status?${querystring.stringify(props)}`
  )(units)

export const hydroProcess = targetInfo => ({ units, ...props }) =>
  post(targetInfo, `hydro/process?${querystring.stringify(props)}`)(units)

export const gethydroEvents = targetInfo => ({ unitIds, ...props }) =>
  post(targetInfo, `hydro/events?${querystring.stringify(props)}`)(unitIds)

export const getLasthydroEvent = targetInfo => data =>
  get(targetInfo, `hydro/last-event?${querystring.stringify(data)}`)

export const savehydroEvents = targetInfo => data =>
  post(targetInfo, 'hydro/events-save')(data)

export const hydroReImport = targetInfo => data =>
  post(targetInfo, 'hydro/reimport')(data)

export const getNercGroup = targetInfo => date =>
  get(targetInfo, 'admin/nerc-group?date=' + date)

export const saveNercGroup = targetInfo => data =>
  post(targetInfo, 'admin/nerc-group')(data)

export const createNercGroup = targetInfo => data =>
  post(targetInfo, 'admin/nerc-group/new')(data)

export const deleteNercGroup = targetInfo => data =>
  post(targetInfo, 'admin/nerc-group/del')(data)

export const getCompletedReport = targetInfo => data =>
  get(targetInfo, `report/completed-report?${querystring.stringify(data)}`)

export const reportCleanFiles = targetInfo =>
  post(targetInfo, 'report/cleanup')()

export const getMwImportData = targetInfo => data =>
  get(targetInfo, `interface-data/mw-import?${querystring.stringify(data)}`)

export const getBreakerData = targetInfo => data =>
  get(
    targetInfo,
    `interface-data/breaker-import?${querystring.stringify(data)}`
  )

export const getOwnershipAccess = targetInfo => data =>
  get(targetInfo, `admin/ownership-access?${querystring.stringify(data)}`)

export const saveOwnershipAccess = targetInfo => data =>
  post(targetInfo, 'admin/ownership-access')(data)
export const deleteOwnershipAccess = targetInfo => data =>
  post(targetInfo, 'admin/ownership-access/del')(data)

export const getContribCondtition = targetInfo =>
  get(targetInfo, `common/contrib-condition`)
export const saveContribCondition = targetInfo => data =>
  post(targetInfo, 'common/contrib-condition')(data)
export const deleteContribCondition = targetInfo => data =>
  post(targetInfo, 'common/contrib-condition/del')(data)

export const transformUserGroupToRole = targetInfo =>
  post(targetInfo, 'transform')()

export const renameRole = (targetInfo, oldName, newName) =>
  get(
    targetInfo,
    `gads-auth/rename?${querystring.stringify({ oldName, newName })}`
  )

export const getReportStations = targetInfo => dates =>
  get(targetInfo, `report/report-stations?${querystring.stringify(dates)}`)

export const getCompanyType = targetInfo =>
  get(targetInfo, 'admin/company-type')

export const getCompanyUserMapping = targetInfo =>
  get(targetInfo, 'common/user-company')

export const getGitVersionsInformation = targetInfo =>
  get(targetInfo, 'core/utils/versions')

export const getSwitchBoardONS = targetInfo => date =>
  get(
    targetInfo,
    `switchboard/ons?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchBoardMNS = targetInfo => date =>
  get(
    targetInfo,
    `switchboard/mns?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchBoardCNS = targetInfo => date =>
  get(
    targetInfo,
    `switchboard/cns?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchBoardHNP = targetInfo => date =>
  get(
    targetInfo,
    `switchboard/hnp?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchBoardBNP = targetInfo => date =>
  get(
    targetInfo,
    `switchboard/bnp?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchBoardRNP = targetInfo => date =>
  get(
    targetInfo,
    `switchboard/rnp?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchboardLOG = targetInfo => (date1, date2) =>
  get(
    targetInfo,
    `switchboard/log?${querystring.stringify({
      date1: date1.format('YYYY-MM-DD'),
      date2: date2.format('YYYY-MM-DD'),
    })}`
  )

export const getSwitchboardValidationLog = targetInfo => (date1, date2) =>
  get(
    targetInfo,
    `switchboard/validation-daily-log?${querystring.stringify({
      date1: date1.format('YYYY-MM-DD'),
      date2: date2.format('YYYY-MM-DD'),
    })}`
  )

export const getMonthlyPerfData = targetInfo => date =>
  get(
    targetInfo,
    `monthlyperfdata?${querystring.stringify({
      YEAR: date.format('YYYY'),
      MONTH: date.format('MM'),
    })}`
  )

export const saveMonthlyPerfData = targetInfo => (data, date) =>
  post(
    targetInfo,
    `monthlyperfdata?${querystring.stringify({
      YEAR: date.format('YYYY'),
      MONTH: date.format('MM'),
    })}`
  )(data)

// data is in array
export const qaMonthlyPerfData = targetInfo => (data, date, all) =>
  post(
    targetInfo,
    `monthlyperfdata/qa?${querystring.stringify({
      YEAR: date.format('YYYY'),
      MONTH: date.format('MM'),
      all,
    })}`
  )(data)

export const saveMonthlyPerfMeterData = targetInfo => (data, date) =>
  post(
    targetInfo,
    `monthlyperfdata/meterdata?${querystring.stringify({
      YEAR: date.format('YYYY'),
      MONTH: date.format('MM'),
    })}`
  )(data)

export const getDukeCard97 = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return get(
    targetInfo,
    `duke/card97?${querystring.stringify({
      year,
      utility,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventTypes,
    })}`
  )
}

export const getDukeCard97ByPost = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `duke/card97/bypost?${querystring.stringify({
      year,
      utility,

      eventTypes,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)
}

export const postDukeCard97 = targetInfo => data =>
  post(targetInfo, 'duke/card97')(data)

export const deleteDukeCard97 = targetInfo => data =>
  post(targetInfo, 'duke/card97/del')(data)

export const getDukeCard97DataCheck = targetInfo => ({
  year,
  units,
  eventnum,
}) =>
  get(
    targetInfo,
    `duke/card97-datacheck?${querystring.stringify({
      year,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventnum,
    })}`
  )

export const getDukeCard97ExtensionData = targetInfo => data =>
  get(targetInfo, `duke/card97-ext-data?${querystring.stringify(data)}`)

export const card97Exclusion = targetInfo => (year, units) =>
  post(targetInfo, `cards/card97-exclusion?year=${year}`)(units)

export const saveCard97Exclusion = targetInfo => data =>
  post(targetInfo, 'cards/card97-exclusion/save')(data)

export const getFileServerClientKey = targetInfo => () =>
  get(targetInfo, `file-server/file-server-client-api-key`)

export const processDataWithFileUpload = targetInfo => ({
  file,
  fileName,
  fileDate,
  id1,
  id2,
  apiKey,
}) =>
  createFetchTask({
    method: 'POST',
    ...getCredentials(targetInfo),
    headers: { 'Content-Type': file.type, ...getAuthorization(targetInfo) },
  })(
    { ...targetInfo, jwt: apiKey },
    `file-server/upload?${querystring.stringify({
      'file-name': fileName,
      'file-date': fileDate && fileDate.format('YYYY-MM-DD'),
      id1,
      id2,
    })}`
  )(file)
export const viewHourlyDerate = targetInfo => ({ units, startDate, endDate }) =>
  post(
    targetInfo,
    `derate/view-hourly?${querystring.stringify({
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.unitId))

export const processHourlyDerate = targetInfo => ({
  units,
  startDate,
  endDate,
}) =>
  post(
    targetInfo,
    `derate/process-hourly?${querystring.stringify({
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.utility + '-' + u.unit))

export const processDerateNYISO = targetInfo => data =>
  post(
    targetInfo,
    `derate/process-derate-nyiso?${querystring.stringify(data)}`
  )(data.units.map(u => u.utility + '-' + u.unit))

export const postGenerationRequest = targetInfo => ({
  units,
  startDate,
  endDate,
}) =>
  post(
    targetInfo,
    `aep/genrequest?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.utility + '-' + u.unit))

export const postFuelRequest = targetInfo => ({ units, month, year }) =>
  post(
    targetInfo,
    `aep/fuelrequest?${querystring.stringify({
      month,
      year,
    })}`
  )(units.map(u => u.utility + '-' + u.unit))

export const getWSAccessInfo = targetInfo =>
  get(targetInfo, 'admin/web-service-access')

export const saveWSAccessInfo = targetInfo => data =>
  post(targetInfo, 'admin/web-service-access')(data)

export const deleteWSAccessInfo = targetInfo => data =>
  post(targetInfo, 'admin/web-service-access/delete')(data)

export const activeStatusUpdateInfo = targetInfo => data =>
  post(targetInfo, 'admin/web-service-access/update-active-status')(data)

export const getLog = targetInfo => date =>
  get(
    targetInfo,
    `admin/weblog?${querystring.stringify({
      transDate: date.format('YYYY-MM-DD'),
    })}`
  )

export const getTpsFileLog = targetInfo => date =>
  get(
    targetInfo,
    `tpscall/weblog?${querystring.stringify({
      transDate: date.format('YYYY-MM-DD'),
    })}`
  )

export const getLogBlob = targetInfo => ({ transId, type }) =>
  get(
    targetInfo,
    `admin/weblog/blob?${querystring.stringify({
      transId,
      type,
    })}`
  )

export const getTpsFileLogBlob = targetInfo => ({ transId, type }) =>
  get(
    targetInfo,
    `tpscall/weblog/blob?${querystring.stringify({
      transId,
      type,
    })}`
  )

export const viewHourlyDerateCauseCode = targetInfo => ({
  units,
  startDate,
  endDate,
}) =>
  post(
    targetInfo,
    `derate/view-hourly-causecode?${querystring.stringify({
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.unitId))

export const processHourlyDerateCauseCode = targetInfo => ({
  units,
  startDate,
  endDate,
}) =>
  post(
    targetInfo,
    `derate/process-hourly-causecode?${querystring.stringify({
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.utility + '-' + u.unit))

export const getViewGeneration = targetInfo => ({
  units,
  startDate,
  endDate,
}) =>
  post(
    targetInfo,
    `aep/view-generation?${querystring.stringify({
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.utility + u.unit))

export const PostViewGenerationInfo = targetInfo => data =>
  post(targetInfo, 'aep/view_generation/save')(data)

export const deleteViewGenerationInfo = targetInfo => data =>
  post(targetInfo, 'aep/view_generation/delete')(data)

export const getGenerationAux = targetInfo => ({ units, startDate, endDate }) =>
  post(
    targetInfo,
    `common/generationaux?${querystring.stringify({
      START: startDate.format('YYYY-MM-DD'),
      END: endDate.format('YYYY-MM-DD'),
    })}`
  )(units.map(u => u.utility + u.unit))

export const postGenerationAuxInfo = targetInfo => data =>
  post(targetInfo, 'common/generationaux/save')(data)

export const deleteGenerationAuxInfo = targetInfo => data =>
  post(targetInfo, 'common/generationaux/delete')(data)

export const getValidationMonthly = targetInfo => (
  year,
  month,
  utility,
  units,
  sevrity
) =>
  post(
    targetInfo,
    `gads-validation/validation-monthly?${querystring.stringify({
      month: month == null ? '' : month,
      year: year == null ? '' : year,
      utility: utility == null ? '' : utility,
      // units: units === undefined || units.length <= 0 ? '' : units,
      sevrity,
    })}`
  )(units)

export const getWindUtilitySelection = targetInfo =>
  get(targetInfo, 'windpowerdata/utility-selection')

export const getWindRegion = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-region')

export const getWindCountry = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-country')

export const getwindSubgroupEffective = targetInfo => date =>
  get(
    targetInfo,
    `windpowerdata/subgroup-effective?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getWindComponent = targetInfo =>
  get(targetInfo, 'windpowerdata/component')

export const getWindUnitStatusType = targetInfo =>
  get(targetInfo, 'windpowerdata/unit-status-type')

export const getWindPowerData = targetInfo => ({ date, selection }) =>
  get(
    targetInfo,
    `windpowerdata/windpowerdata?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
      selection,
    })}`
  )

export const postWindPowerData = targetInfo => data =>
  post(targetInfo, 'windpowerdata/windpowerdata')(data)

export const windLevel2validation = targetInfo => ({ data, year, month }) =>
  post(
    targetInfo,
    `windpowerdata/windlevel2validation?${querystring.stringify({
      year,
      month,
    })}`
  )(data)

export const windExportPerformanceData = targetInfo => ({
  fileType,
  startDate,
  endDate,
  data,
}) =>
  post(
    targetInfo,
    `windpowerdata/exportnercfiles?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      fileType,
    })}`
  )(data)

export const windColMetaInfo = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-col-meta-info')

export const windReportGroup = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-report-group')

export const windReportSubGroupTemplate = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-report-subgroup-template')

export const saveWindRptDataItem = targetInfo => data =>
  post(targetInfo, `windpowerdata/update-rpt-dataitem-group`)(data)

export const saveWindRptSubgroup = targetInfo => data =>
  post(targetInfo, `windpowerdata/update-rpt-subgroup-group`)(data)

export const generateAdhocReport = targetInfo => (data, reportType) =>
  post(
    targetInfo,
    `windpowerdata/wind-adhoc-report-generate?${querystring.stringify({
      reportType,
    })}`
  )(data)

export const getWindPlantInfo = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-plant')

export const postWindPlantInfo = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-plant/save')(data)

export const deleteWindPlantInfo = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-plant/del')(data)

export const getWindUtilityOperatorInfo = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-utility-operator')

export const postWindUtilityOperatorInfo = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-utility-operator/save')(data)

export const deleteWindUtilityOperatorInfo = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-utility-operator/del')(data)

export const getWindSubgroupInactive = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-subgroup-inactive')

export const postWindSubgroupInactive = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-subgroup-inactive')(data)

export const deleteWindSubgroupInactive = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-subgroup-inactive/del')(data)

export const getWindSubgroup = targetInfo =>
  get(targetInfo, 'windpowerdata/subgroup')

export const saveWindSubgroup = targetInfo => data =>
  post(targetInfo, 'windpowerdata/subgroup')(data)

export const deleteWindSubgroup = targetInfo => data =>
  post(targetInfo, 'windpowerdata/subgroup/del')(data)

export const getWindUnitAccess = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-unit-access')

export const postWindUnitAccess = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-unit-access')(data)

export const deleteWindUnitAccess = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-unit-access/del')(data)

export const getStateCountry = targetInfo => get(targetInfo, 'admin/state')

export const getWindRegime = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-regime')

export const getWindScada = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-scada')

export const getWindTurbineManufacture = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-turbinemanufacture')

export const getWindTurbulence = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-turbulence')

export const getWindShear = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-shear')

export const getWindOwnershipStatus = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-ownership-status')

export const getWindGadsErrorMgmtInfo = targetInfo =>
  get(targetInfo, 'windpowerdata/wind-gads-error')

export const postWindGadsErrorMgmtInfo = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-gads-error')(data)

export const deleteWindGadsErrorMgmtInfo = targetInfo => data =>
  post(targetInfo, 'windpowerdata/wind-gads-error/del')(data)

export const getOperatingData = targetInfo =>
  get(targetInfo, 'admin/operating-data')

export const saveOperatingData = targetInfo => data =>
  post(targetInfo, 'admin/operating-data')(data)

export const getPiLog = targetInfo => data =>
  get(
    targetInfo,
    `pi/pilog?${querystring.stringify({
      date: data.format('YYYY-MM-DD'),
    })}`
  )

export const getPiLogBlob = targetInfo => data =>
  get(
    targetInfo,
    `pi/download-pilog-blob?${querystring.stringify({
      id: data.id,
      ident: data.ident,
    })}`
  )

export const getPiTags = targetInfo => data =>
  get(
    targetInfo,
    `pi/pitag?${querystring.stringify({
      date: data.format('YYYY-MM-DD'),
    })}`
  )

export const getPiTagsActive = targetInfo => date =>
  get(
    targetInfo,
    `pi/pitag-active?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const getPiData = targetInfo => data =>
  get(
    targetInfo,
    `pi/pidata?${querystring.stringify({
      startDate: data.startDate.format('YYYY-MM-DD'),
      endDate: data.endDate.format('YYYY-MM-DD'),
      tagnames: data.tagnames,
    })}`
  )

export const importPiData = targetInfo => data =>
  get(
    targetInfo,
    `pi/importpidata?${querystring.stringify({
      startDate: data.startDate.format('YYYY-MM-DD'),
      endDate: data.endDate.format('YYYY-MM-DD'),
      tagnames: data.tagnames,
    })}`
  )

export const getPiTagTypes = targetInfo => get(targetInfo, 'pi/pitag-types')
export const getPiWindPlants = targetInfo => () =>
  get(targetInfo, 'pi/pi-windplant')
export const getPiwindPlantsSubgroup = targetInfo => () =>
  get(targetInfo, 'pi/pi-windplant-subgroup')

export const savePiTag = targetInfo => data =>
  post(targetInfo, 'pi/pitag-save')(data)
export const deletePiTag = targetInfo => data =>
  post(targetInfo, 'pi/pitag-delete')(data)

export const getPiMonSumm = targetInfo => data =>
  get(
    targetInfo,
    `pi/pimonsumm?${querystring.stringify({
      year: data.year,
      month: data.month,
      plantId: data.plantId,
      utility: data.utility,
    })}`
  )

/* export const savePiMonSumm = targetInfo => data =>
  post(
    targetInfo,
    `pi/pimonsumm/upsert?${querystring.stringify({
      newData: data.saveData,
    })}`
  )(data) */

export const savePiMonSumm = targetInfo => data =>
  post(targetInfo, 'pi/pimonsumm/upsert')(data)

/* export const finalizePiMonSumm = targetInfo => data =>
  post(targetInfo, 'pi/pimonsumm/finalize')({}) */

export const finalizePiMonSumm = targetInfo => req =>
  get(
    targetInfo,
    `pi/pimonsumm/finalize?${querystring.stringify({
      year: req.year,
      month: req.month,
      plantId: req.plantId,
      utility: req.utility,
    })}`
  )

export const processAdhocDbReport = targetInfo => ({ month, year }) =>
  get(
    targetInfo,
    `adhoc-report/adhoc-report-process?${querystring.stringify({
      month: month == null ? '' : month,
      year: year == null ? '' : year,
    })}`
  )

export const lockCard95Data = targetInfo => ({ units, ...data }) =>
  post(targetInfo, `cards/card95-lock?${querystring.stringify(data)}`)(units)

export const lockCard99Data = targetInfo => ({ units, ...data }) =>
  post(targetInfo, `cards/card99-lock?${querystring.stringify(data)}`)(units)

export const getBkfCard97 = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return get(
    targetInfo,
    `bkf/card97?${querystring.stringify({
      year,
      utility,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventTypes,
    })}`
  )
}

export const getBkfCard97ByPost = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `bkf/card97data?${querystring.stringify({
      year,
      utility,

      eventTypes,
    })}`
  )(units === undefined || units.length <= 0 ? '' : units)
}

export const getBKFCard97DataCheck = targetInfo => ({
  year,
  units,
  eventnum,
}) =>
  get(
    targetInfo,
    `bkf/card97-datacheck?${querystring.stringify({
      year,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventnum,
    })}`
  )

export const getNRGCard97DataCheck = targetInfo => ({
  year,
  units,
  eventnum,
}) =>
  get(
    targetInfo,
    `nrg/card97-datacheck?${querystring.stringify({
      year,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventnum,
    })}`
  )

export const getDominionCard97 = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `dominion/card97?${querystring.stringify({
      year,
      utility,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventTypes,
    })}`
  )
}

export const domonionCard97Attachment = targetInfo => data =>
  post(
    targetInfo,
    `dominion/dom-card97-attach-file-delete?${querystring.stringify(data)}`
  )()
export const domonionCard97AttachmentDownload = targetInfo => data =>
  get(
    targetInfo,
    `dominion/download-card97-attachment-file?${querystring.stringify(data)}`
  )

export const getDominionCard97ByPost = targetInfo => (
  year,
  utility,
  units,
  eventTypes
) => {
  eventTypes =
    eventTypes === undefined ? eventTypesMap.map(e => e.value) : eventTypes
  return post(
    targetInfo,
    `dominion/card97/bypost?${querystring.stringify({
      year,
      utility,
      // units: units === undefined || units.length <= 0 ? '' : units,
      eventTypes,
    })}`
  )(units)
}

export const postDominionCard97 = targetInfo => data =>
  post(targetInfo, 'dominion/card97')(data)

export const postBrookfieldCard97 = targetInfo => data =>
  post(targetInfo, 'bkf/card97')(data)
export const postNRGCard97 = targetInfo => data =>
  post(targetInfo, 'nrg/card97')(data)
export const postBrookfieldCard97Delete = targetInfo => data =>
  post(targetInfo, 'bkf/card97/del')(data)
export const postNRGCard97Delete = targetInfo => data =>
  post(targetInfo, 'nrg/card97/del')(data)

export const deleteDominionCard97 = targetInfo => data =>
  post(targetInfo, 'dominion/card97/del')(data)

export const getDominionCard97DataCheck = targetInfo => ({
  year,
  units,
  eventnum,
}) =>
  get(
    targetInfo,
    `dominion/card97-datacheck?${querystring.stringify({
      year,
      units: units === undefined || units.length <= 0 ? '' : units,
      eventnum,
    })}`
  )

export const getDominionCard97ExtensionData = targetInfo => data =>
  get(targetInfo, `dominion/card97-ext-data?${querystring.stringify(data)}`)

export const getBrookfieldCard97ExtensionData = targetInfo => data =>
  get(targetInfo, `bkf/card97-ext-data?${querystring.stringify(data)}`)

export const getNRGCard97ExtensionData = targetInfo => data =>
  get(targetInfo, `nrg/card97-ext-data?${querystring.stringify(data)}`)

export const performanceOverideReport = targetInfo =>
  get(targetInfo, 'report/performance-override')

export const performanceOverideReportSave = targetInfo => data =>
  post(targetInfo, 'report/performance-override')(data)

export const performanceOverideReportDelete = targetInfo => data =>
  post(targetInfo, 'report/performance-override/delete')(data)
export const getTurbinePointsInterface = targetInfo =>
  get(targetInfo, 'interface-data/turbine-points')

export const postTurbinePointsInterface = targetInfo => data =>
  post(targetInfo, 'interface-data/turbine-points')(data)

export const deleteTurbinePointsInterface = targetInfo => data =>
  post(targetInfo, 'interface-data/turbine-points/delete')(data)

export const getEdnaErrocodeMapInterface = targetInfo =>
  get(targetInfo, 'interface-data/bsgf-ghwp-error-code-map')

export const saveEdnaErrocodeMapInterface = targetInfo => data =>
  post(targetInfo, 'interface-data/bsgf-ghwp-error-code-map')(data)

export const deleteEdnaErrocodeMapInterface = targetInfo => data =>
  post(targetInfo, 'interface-data/bsgf-ghwp-error-code-map/delete')(data)

export const getCraneCreekInterfaceImport = targetInfo => data =>
  get(targetInfo, `interface-data/crane-creek?${querystring.stringify(data)}`)

export const getForwardInterfaceImport = targetInfo => data =>
  get(
    targetInfo,
    `interface-data/forward-import?${querystring.stringify(data)}`
  )

export const getBSGFImport = targetInfo => data =>
  get(targetInfo, `interface-data/bsgf?${querystring.stringify(data)}`)

export const getGHWPImport = targetInfo => data =>
  get(targetInfo, `interface-data/ghwp?${querystring.stringify(data)}`)

export const gettGenerationHourly = targetInfo => data =>
  get(targetInfo, `common/generation-hourly?${querystring.stringify(data)}`)

export const saveGenerationHourly = targetInfo => data =>
  post(targetInfo, 'common/generation-hourly')(data)

export const deleteGenerationHourly = targetInfo => data =>
  post(targetInfo, 'common/generation-hourly/delete')(data)

export const getDateHourList = targetInfo => (startDate, endDate) =>
  get(
    targetInfo,
    `common/data-hour-list?${querystring.stringify({
      startdate: startDate.format('YYYY-MM-DD'),
      enddate: endDate.format('YYYY-MM-DD'),
    })}`
  )
export const getMarginAnalysisScnerio = targetInfo =>
  get(targetInfo, 'marginanalysis/scenario')

export const deleteMarginAnalysisScnerio = targetInfo => id =>
  get(targetInfo, `marginanalysis/scenario/delete?scenarioid=` + id)

export const getMarginAnalysisRetrieveData = targetInfo => data =>
  post(targetInfo, 'marginanalysis')(data)

export const getMarginAnalysisEventPlanningRetrieveData = targetInfo => data =>
  post(targetInfo, 'marginanalysis/event-planning')(data)

export const marginAnalysisExportData = targetInfo => data =>
  post(targetInfo, 'marginanalysis/export-pdf')(data)

export const marginAnalysisExportBlob = targetInfo => (
  data,
  qryParam,
  filename
) => otherFunc => {
  const URL = `${
    targetInfo.baseUrl
  }/marginanalysis/export-pdf?${querystring.stringify(qryParam)}`
  return fetch(`${URL}`, {
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      ...getAuthorization(targetInfo),
    },
    body: JSON.stringify(data),
  })
    .then(response => response.blob())
    .then(blob => exportData(blob, 'application/pdf', filename))
    .then(b => (otherFunc ? otherFunc(b) : b))
}

export const marginAnalysisTrueExportBlob = targetInfo => (
  data,
  qryParam,
  filename
) => otherFunc => {
  const URL = `${
    targetInfo.baseUrl
  }/marginanalysis/trueup-export-pdf?${querystring.stringify(qryParam)}`
  return fetch(`${URL}`, {
    method: 'POST',
    ignoreAuth: false,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      ...getAuthorization(targetInfo),
    },
    body: JSON.stringify(data),
  })
    .then(response => response.blob())
    .then(blob => exportData(blob, 'application/pdf', filename))
    .then(b => (otherFunc ? otherFunc(b) : b))
}

export const getMarginAnalysisRecallRetrieveData = targetInfo => data =>
  post(targetInfo, 'marginanalysis/recall')(data)

export const marginAnalysisEventPlanner = targetInfo => data =>
  post(targetInfo, 'marginanalysis/export-eventplanner')(data)

export const marginAnalysisSaveTicket = targetInfo => data =>
  post(targetInfo, 'marginanalysis/save-event-data-ticket')(data)

export const marginAnalysisValidate = targetInfo => data =>
  post(targetInfo, 'marginanalysis/validate-analysis')(data)

export const marginAnalysisValidateBS = targetInfo => data =>
  post(targetInfo, 'marginanalysis/validate-analysis/whatif-bs')(data)

export const saveMarginAnalysisScnerio = targetInfo => data =>
  post(targetInfo, 'marginanalysis/save-data')(data)

export const marginPnodeZone = targetInfo =>
  get(targetInfo, 'marginanalysisdata/margin-pnode-zone')

export const marginPnodeZoneEffectieve = targetInfo =>
  get(targetInfo, 'marginanalysisdata/margin-pnode-zone-effective')

export const marginPnodeZoneUpdate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/margin-pnode-zone')(data)

export const marginPnodeZoneDelete = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/margin-pnode-zone/delete')(data)

export const marginPnodeZoneAEP = targetInfo =>
  get(targetInfo, 'marginanalysisdata/margin-pnode-zone-aep')

export const marginPnodeZoneUpdateAEP = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/margin-pnode-zone-aep')(data)

export const marginPnodeZoneDeleteAEP = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/margin-pnode-zone-aep/delete')(data)

export const marginiPeakDefinition = targetInfo =>
  get(targetInfo, 'marginanalysisdata/margin-peak-definition')

export const marginiPeakDefinitionUpdate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/margin-peak-definition')(data)

export const marginiPeakDefinitionDelete = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/margin-peak-definition/delete')(data)

export const marginDaPrice = targetInfo => data =>
  get(targetInfo, `marginanalysisdata/da-price?${querystring.stringify(data)}`)

export const marginDaPriceUpdate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/da-price')(data)

export const marginDaPriceDelete = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/da-price/delete')(data)

export const marginDaPriceInsert = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/da-price/insert')(data)

export const marginRtPrice = targetInfo => data =>
  get(targetInfo, `marginanalysisdata/rt-price?${querystring.stringify(data)}`)

export const marginRtPriceUpdate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/rt-price')(data)

export const marginRtPriceDelete = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/rt-price/delete')(data)

export const marginRtPriceInsert = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/rt-price/insert')(data)

export const retrieveHeatCurveSeason = targetInfo =>
  get(targetInfo, `marginanalysisdata/heatcurve-season`)

export const retrieveHeatCurve = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/heatcurve?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveHeatCurve = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/heatcurve')(data)

export const deleteHeatCurve = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/heatcurve/delete')(data)

export const retrieveHeatCurveDOM = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/heatcurve/dom?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveHeatCurveDOM = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/heatcurve/dom')(data)

export const deleteHeatCurveDOM = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/heatcurve/delete/dom')(data)

export const getPFuelCodeAtFuelCosts = targetInfo =>
  get(targetInfo, `marginanalysisdata/fuelcosts-pfuelcode`)

export const geFuelCodeByFuelCostsTable = targetInfo =>
  get(targetInfo, `marginanalysisdata/pfuelcode-by-fuelcost`)

export const getPFuelUnitsAtFuelCosts = targetInfo =>
  get(targetInfo, `marginanalysisdata/fuelcosts-unitid`)

export const getFuelCosts = targetInfo => data =>
  get(targetInfo, `marginanalysisdata/fuelcosts?${querystring.stringify(data)}`)

export const getFuelCostsBySystemModifier = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/fuelcosts/systemmodifier?${querystring.stringify(data)}`
  )

export const saveFuelCosts = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/fuelcosts')(data)

export const deleteFuelCosts = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/fuelcosts/delete')(data)

export const getStartFuelCodeAtStartupCost = targetInfo =>
  get(targetInfo, `marginanalysisdata/startupcost-startfuelcode`)

export const retrieveFuelUsage = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/fuelusage?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveFuelUsage = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/fuelusage')(data)

export const deleteFuelUsage = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/fuelusage/delete')(data)

export const retrieveCostAdjustment = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/cost-adjustment?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveCostAdjustment = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/cost-adjustment')(data)

export const deleteCostAdjustment = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/cost-adjustment/delete')(data)

export const retrieveStartupOm = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/startupom?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveStartupOm = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startupom')(data)

export const deleteStartupOm = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startupom/delete')(data)

export const getPFuelCodeAtOtherCosts = targetInfo =>
  get(targetInfo, `marginanalysisdata/othercosts-pfuelcode`)

export const retrieveOtherCosts = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/othercosts?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveOtherCosts = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/othercosts')(data)

export const deleteOtherCosts = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/othercosts/delete')(data)

export const retrieveZainetFuelPrice = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/zainet-fuelprice?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveZainetFuelPrice = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/zainet-fuelprice/save')(data)

export const deleteZainetFuelPrice = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/zainet-fuelprice/delete')(data)

export const retrieveUnavailableFactor = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/unavailable-factor?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveUnavailableFactor = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/unavailable-factor/save')(data)

export const deleteUnavailableFactor = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/unavailable-factor/delete')(data)

export const retrieveBudgetConstraints = targetInfo => ({
  date,
  year,
  units,
}) =>
  get(
    targetInfo,
    `marginanalysisdata/budget-constraints?${querystring.stringify({
      year,
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveBudgetConstraints = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-constraints/save')(data)

export const deleteBudgetConstraints = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-constraints/delete')(data)

export const retrieveBudgetFuelUsage = targetInfo => ({ date, year, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/budget-fuelusage?${querystring.stringify({
      year,
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveBudgetFuelUsage = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-fuelusage/save')(data)

export const deleteBudgetFuelUsage = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-fuelusage/delete')(data)

export const retrieveBudgetData = targetInfo => ({ month, year, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/budget-data?${querystring.stringify({
      year,
      month,
      units,
    })}`
  )

export const saveBudgetData = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-data/save')(data)

export const deleteBudgetData = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-data/delete')(data)

export const retrievePlannedEvent = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/planned-event?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const savePlannedEvent = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/planned-event/save')(data)

export const deletePlannedEvent = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/planned-event/delete')(data)

export const retrieveGoalCA = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/goal-ca?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveGoalCA = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/goal-ca/save')(data)

export const deleteGoalCA = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/goal-ca/delete')(data)

export const retrieveVomCost = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/vom-cost?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveVomCost = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/vom-cost/save')(data)

export const deleteVomCost = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/vom-cost/delete')(data)

export const retrieveEfor = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/efor?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveEfor = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/efor/save')(data)

export const deleteEfor = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/efor/delete')(data)

export const retrieveFleetPeak = targetInfo => ({ units }) =>
  get(
    targetInfo,
    `marginanalysisdata/fleet-peak?${querystring.stringify({
      units,
    })}`
  )

export const saveFleetPeak = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/fleet-peak/save')(data)

export const deleteFleetPeak = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/fleet-peak/delete')(data)

export const retrieveTrueupAdjustment = targetInfo => ({
  month,
  year,
  units,
}) =>
  get(
    targetInfo,
    `marginanalysisdata/trueup-adjustment?${querystring.stringify({
      year,
      month,
      units,
    })}`
  )

export const saveTrueupAdjustment = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/trueup-adjustment/save')(data)

export const deleteTrueupAdjustment = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/trueup-adjustment/delete')(data)

export const retrieveStartupHeat = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/startup-heat?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveStartupHeat = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startup-heat/save')(data)

export const deleteStartupHeat = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startup-heat/delete')(data)

export const retrieveBudgetHeatCurves = targetInfo => ({ date, year, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/budget-heatcurves?${querystring.stringify({
      year,
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveBudgetHeatCurves = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-heatcurves/save')(data)

export const deleteBudgetHeatCurves = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-heatcurves/delete')(data)

export const retrieveBudgetRampRate = targetInfo => ({ date, year, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/budget-ramprate?${querystring.stringify({
      year,
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveBudgetRampRate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-ramprate/save')(data)

export const deleteBudgetRampRate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/budget-ramprate/delete')(data)

export const retrieveRampRate = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/ramprate?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveRampRate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/ramprate')(data)

export const deleteRampRate = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/ramprate/delete')(data)

export const retrieveConstraints = targetInfo => ({
  startDate,
  endDate,
  units,
  byEff = false,
  effectieveDate,
}) => {
  return get(
    targetInfo,
    `marginanalysisdata/constraints${
      byEff ? '-eff' : ''
    }?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      units,
      effectieveDate: effectieveDate.format('YYYY-MM-DD'),
    })}`
  )
}

export const saveConstraints = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/constraints')(data)

export const deleteConstraints = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/constraints/delete')(data)

export const retrieveStartupCost = targetInfo => ({
  startDate,
  endDate,
  units,
}) =>
  get(
    targetInfo,
    `marginanalysisdata/startupcost?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      units,
    })}`
  )

export const saveStartupCost = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startupcost')(data)

export const deleteStartupCost = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startupcost/delete')(data)

export const oaFuelCostMissingData = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/fuelcost-missing?${querystring.stringify(data)}`
  )

export const retrieveDayAheadAward = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/dayaheadaward?${querystring.stringify(data)}`
  )

export const saveDayAheadAward = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/dayaheadaward')(data)

export const deleteDayAheadAward = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/dayaheadaward/delete')(data)

export const retrieveAncillaryCredits = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/ancillary-credit?${querystring.stringify(data)}`
  )

export const saveAncillaryCredits = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/ancillary-credit/save')(data)

export const deleteAncillaryCredits = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/ancillary-credit/delete')(data)

export const deleteAdhocUnitGroup = targetInfo => groupname =>
  get(targetInfo, `adhoc-report/delete-unit-group?groupname=` + groupname)

export const deleteAdhocDataItemGroup = targetInfo => groupname =>
  get(targetInfo, `adhoc-report/delete-dataitem-group?groupname=` + groupname)

export const deleteAdhocCauseCodeGroup = targetInfo => groupname =>
  get(targetInfo, `adhoc-report/delete-causecode-group?groupname=` + groupname)

export const retrieveOaDaImportData = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/dayaheadlmp?${querystring.stringify(data)}`
  )

export const retrieveOaRtImportData = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/realtimelmp?${querystring.stringify(data)}`
  )

export const retrieveOaLMPPromodImportData = targetInfo => data =>
  get(targetInfo, `marginanalysisdata/lmppromod?${querystring.stringify(data)}`)

export const getStartupCredit = targetInfo => data =>
  get(
    targetInfo,
    `marginanalysisdata/startupcredit?${querystring.stringify(data)}`
  )

export const saveStartupCredit = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startupcredit')(data)

export const deleteStartupCredit = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/startupcredit/delete')(data)

export const oaDaLmpImportData = targetInfo => (startdate, hour) =>
  get(
    targetInfo,
    'dataminer/dalmp-data-download?hour=' + hour + '&startdate=' + startdate
  )

export const oaRTLmpImportData = targetInfo => (startdate, hour, min) =>
  get(
    targetInfo,
    'dataminer/rtlmp-data-download?min=' +
      min +
      '&hour=' +
      hour +
      '&startdate=' +
      startdate
  )

export const oaDaLmpScheduler = targetInfo =>
  post(targetInfo, 'dataminer/dalmp-datadownload-scheduler')()

export const retrieveHydroInputs = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/hydroinputs?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )
export const saveHydroInputs = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/hydroinputs')(data)
export const deleteHydroInputs = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/hydroinputs/delete')(data)

export const retrieveLMOAdjustment = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/lmoadjustment?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )
export const saveLMOAdjustment = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/lmoadjustment')(data)
export const deleteLMOAdjustment = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/lmoadjustment/delete')(data)

export const saveLMOAdjustmentEvent = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/lmoadjustment/analytics')(data)

export const deleteLMOAdjustmentEvent = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/lmoadjustment/analytics/delete')(data)

export const getLMOAdjustmentEvent = targetInfo => ({ units, year }) =>
  post(
    targetInfo,
    `marginanalysisdata/lmoadjustment/analytics/get?${querystring.stringify({
      year,
    })}`
  )(units)

export const retrieveMakeWholeCredit = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/makewholecredit?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )
export const saveMakeWholeCredit = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/makewholecredit')(data)
export const deleteMakeWholeCredit = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/makewholecredit/delete')(data)

export const retrieveRegulationCredit = targetInfo => ({ date, units }) =>
  get(
    targetInfo,
    `marginanalysisdata/regulationcredit?${querystring.stringify({
      effectiveDate: date.format('YYYY-MM-DD'),
      units,
    })}`
  )
export const saveRegulationCredit = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/regulationcredit')(data)
export const deleteRegulationCredit = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/regulationcredit/delete')(data)

export const retrieveOaConstraintsPFuelCodes = targetInfo =>
  get(targetInfo, 'marginanalysisdata/constraints/pfuelcode')

export const gettGenerationHourlyDOM = targetInfo => data =>
  get(targetInfo, `dominion/generation-hourly?${querystring.stringify(data)}`)

export const saveGenerationHourlyDOM = targetInfo => data =>
  post(targetInfo, 'dominion/generation-hourly')(data)

export const deleteGenerationHourlyDOM = targetInfo => data =>
  post(targetInfo, 'dominion/generation-hourly/delete')(data)

export const getGenerationLockCheck = targetInfo => data =>
  get(
    targetInfo,
    `dominion/generation/lockcheck?${querystring.stringify(data)}`
  )

export const getWSAccessInfoWEE = targetInfo =>
  get(targetInfo, 'wee/web-service-access')

export const saveWSAccessInfoWEE = targetInfo => data =>
  post(targetInfo, 'wee/web-service-access')(data)

export const deleteWSAccessInfoWEE = targetInfo => data =>
  post(targetInfo, 'wee/web-service-access/delete')(data)

export const getKindleIntegrationMapping = targetInfo =>
  get(targetInfo, 'kindle-datatransfer/oa-integration-mapping')

export const kindleMySqlGetDALmp = targetInfo => ({
  start,
  end,
  pnodes = ['34497127', '40243807'],
}) =>
  get(
    targetInfo,
    `kindle-datatransfer/da-lmp?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      pnodes,
    })}`
  )

export const kindleMySqlSaveDALmp = targetInfo => ({
  start,
  end,
  pnodes = ['34497127', '40243807'],
}) =>
  post(
    targetInfo,
    `kindle-datatransfer/da-lmp?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      pnodes,
    })}`
  )()

export const kindleMySqlSaveRTLmp = targetInfo => ({
  start,
  end,
  pnodes = ['34497127', '40243807'],
}) =>
  post(
    targetInfo,
    `kindle-datatransfer/rt-lmp?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      pnodes,
    })}`
  )()

export const kindleMySqlGetRTLmp = targetInfo => ({
  start,
  end,
  pnodes = ['34497127', '40243807'],
}) =>
  get(
    targetInfo,
    `kindle-datatransfer/rt-lmp?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      pnodes,
    })}`
  )

export const kindleMySqlGeneration = targetInfo => ({
  start,
  end,
  unitId = [],
}) =>
  get(
    targetInfo,
    `kindle-datatransfer/generation?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      unitId,
    })}`
  )

export const kindleMySqlSaveGeneration = targetInfo => ({
  start,
  end,
  unitId = [],
}) =>
  post(
    targetInfo,
    `kindle-datatransfer/generation?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      unitId,
    })}`
  )()

export const kindleMySqlDaAward = targetInfo => ({
  location = [],
  start,
  end,
}) =>
  get(
    targetInfo,
    `kindle-datatransfer/da-award?${querystring.stringify({
      // date: date.format('YYYY-MM-DD'),
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      location,
    })}`
  )

export const kindleMySqlDaAwardSave = targetInfo => ({
  location = [],
  start,
  end,
}) =>
  post(
    targetInfo,
    `kindle-datatransfer/da-award?${querystring.stringify({
      // date: date.format('YYYY-MM-DD'),
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      location,
    })}`
  )()

export const kindleMySqlFuel = targetInfo => ({ ids = [], start, end }) =>
  get(
    targetInfo,
    `kindle-datatransfer/fuel?${querystring.stringify({
      // date: date.format('YYYY-MM-DD'),
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      ids,
    })}`
  )

export const kindleMySqlFuelSave = targetInfo => ({ ids = [], start, end }) =>
  post(
    targetInfo,
    `kindle-datatransfer/fuel?${querystring.stringify({
      // date: date.format('YYYY-MM-DD'),
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      ids,
    })}`
  )()

export const getSrpSystemCodeCodeInfo = targetInfo =>
  get(targetInfo, 'admin/srp-systemcode')

export const postSrpSystemCodeInfo = targetInfo => data =>
  post(targetInfo, 'admin/srp-systemcode')(data)

export const deleteSrpSystemCodeInfo = targetInfo => data =>
  post(targetInfo, 'admin/srp-systemcode/delete')(data)

export const eventDashboard = targetInfo => data =>
  post(targetInfo, `event-dashboard`)(data)

export const srpEventDashboard = targetInfo => data =>
  post(targetInfo, `event-dashboard/srp-event-dashboard`)(data)
export const BKFConversionFactorEffective = targetInfo => unitid =>
  post(targetInfo, `bkf/conversion-factor-by-unit`)([unitid])

export const BKFConversionFactorEffectiveByStation = targetInfo => stationList =>
  post(targetInfo, `bkf/conversion-factor-by-stations`)(stationList)

export const getRiverUnitGroup = targetInfo => moment =>
  get(
    targetInfo,
    `admin/river-system-unit-group?${querystring.stringify({
      effectivedate: moment.format('YYYY-MM-DD'),
    })}`
  )

export const postRiverUnitGroup = targetInfo => data =>
  post(targetInfo, 'admin/river-system-unit-group')([data])

export const deleteRiverUnitGroup = targetInfo => data =>
  post(targetInfo, 'admin/river-system-unit-group/delete')([data])

export const getRiverRegionGroup = targetInfo => moment =>
  get(
    targetInfo,
    `admin/river-region-group?${querystring.stringify({
      effectivedate: moment.format('YYYY-MM-DD'),
    })}`
  )

export const postRiverRegionGroup = targetInfo => data =>
  post(targetInfo, 'admin/river-region-group')([data])

export const deleteRiverRegionGroup = targetInfo => data =>
  post(targetInfo, 'admin/river-region-group/delete')([data])

export const getAllRiverNames = targetInfo =>
  get(targetInfo, 'admin/river-system')

export const getWsGenerationMW = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `admin/ws-generationmw?${querystring.stringify({
      start: start.format(DATE_FORMAT_API),
      end: end.format(DATE_FORMAT_API),
      unitId: units,
    })}`
  )

export const importWsGenerationMW = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `admin/ws-generationmw/import?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const getWsFuelCost = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `admin/ws-fuelcost?${querystring.stringify({
      start: start.format(DATE_FORMAT_API),
      end: end.format(DATE_FORMAT_API),
      unitId: units,
    })}`
  )

export const importWsFuelCost = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `admin/ws-fuelcost/import?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const getWsMarketPrice = targetInfo => ({ start, end }) =>
  get(
    targetInfo,
    `admin/ws-marketprice?${querystring.stringify({
      start: start.format(DATE_FORMAT_API),
      end: end.format(DATE_FORMAT_API),
    })}`
  )

export const importWsMarketPrice = targetInfo => ({ start, end }) =>
  post(
    targetInfo,
    `admin/ws-marketprice/import?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    })}`
  )()

export const getWsBreakerData = targetInfo => ({ start, end, units, zone }) =>
  get(
    targetInfo,
    `admin/ws-breakerdata?${querystring.stringify({
      start: start.format(DATE_FORMAT_API),
      end: end.format(DATE_FORMAT_API),
      zone,
      unitId: units,
    })}`
  )

export const importWsBreakerData = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `admin/ws-breakerdata/import?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const blockLevel2Processing = targetInfo => data =>
  get(
    targetInfo,
    `gads-validation/block-level2-processing?${querystring.stringify(data)}`
  )
export const getBkfSpillCause = targetInfo => get(targetInfo, 'bkf/spill-cause')
export const getNonOutageSpill = targetInfo => ({ stationIds, year, month }) =>
  post(
    targetInfo,
    `bkf/non-outage-spill?${querystring.stringify({ year, month })}`
  )(stationIds)

export const saveNonOutageSpill = targetInfo => data =>
  post(targetInfo, `bkf/non-outage-spill-save`)(data)

export const deleteNonOutageSpill = targetInfo => data =>
  post(targetInfo, `bkf/non-outage-spill-delete`)(data)

export const getApsCard97MelSelection = targetInfo => unitId =>
  get(targetInfo, `aps/card97-mel-selection?unitid=${unitId}`)
export const getMelCauseCode = targetInfo =>
  get(targetInfo, 'aps/mel-causecode')
export const saveMelCauseCode = targetInfo => data =>
  post(targetInfo, 'aps/mel-causecode')(data)
export const deleteMelCauseCode = targetInfo => data =>
  post(targetInfo, 'aps/mel-causecode/delete')(data)
export const getMelComponent = targetInfo =>
  get(targetInfo, 'aps/mel-component')
export const saveMelComponent = targetInfo => data =>
  post(targetInfo, 'aps/mel-component')(data)
export const deleteMelComponent = targetInfo => data =>
  post(targetInfo, 'aps/mel-component/delete')(data)

export const getStationCodeList = targetInfo =>
  get(targetInfo, 'aps/station-code')

export const getMarginHourlyCA = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `marginanalysisdata/oa-hourly?${querystring.stringify({ start, end })}`
  )(units)

export const getMarginGasPrice = targetInfo => ({ start, end }) =>
  get(
    targetInfo,
    `marginanalysisdata/gas-price-daily?${querystring.stringify({
      start,
      end,
    })}`
  )

export const saveMarginGasPrice = targetInfo => data =>
  post(targetInfo, `marginanalysisdata/gas-price-daily`)(data)

export const deleteMarginGasPrice = targetInfo => data =>
  post(targetInfo, `marginanalysisdata/gas-price-daily/delete`)(data)

export const getForecastData = targetInfo => ({ year, units, allormonth }) =>
  get(
    targetInfo,
    `marginanalysisdata/forecast?${querystring.stringify({
      year,
      month: allormonth,
      units,
    })}`
  )

export const deleteMarginForecast = targetInfo => data =>
  post(targetInfo, `marginanalysisdata/forecast/delete`)(data)

export const saveMarginForecast = targetInfo => data =>
  post(targetInfo, `marginanalysisdata/forecast`)(data)

export const getForecastDataGenon = targetInfo => ({
  year,
  units,
  allormonth,
}) =>
  get(
    targetInfo,
    `marginanalysisdata/forecast/genon?${querystring.stringify({
      year,
      month: allormonth,
      units,
    })}`
  )

export const deleteMarginForecastGenon = targetInfo => data =>
  post(targetInfo, `marginanalysisdata/forecast/genon/delete`)(data)

export const saveMarginForecastGenon = targetInfo => data =>
  post(targetInfo, `marginanalysisdata/forecast/genon`)(data)

export const getBidOfferMarginAnalysis = targetInfo => ({
  startdate,
  enddate,
  unitid,
}) =>
  post(
    targetInfo,
    `marginanalysisdata/bid-offer?${querystring.stringify({
      startdate: startdate.format('YYYY-MM-DD'),
      enddate: enddate.format('YYYY-MM-DD'),
    })}`
  )(unitid)

export const updateBidOfferMarginAnalysis = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/bid-offer/update')(data)
export const deleteBidOfferMarginAnalysis = targetInfo => data =>
  post(targetInfo, 'marginanalysisdata/bid-offer/delete')(data)

export const getRegion = targetInfo => get(targetInfo, 'admin/region')

export const getCtModal = targetInfo => get(targetInfo, 'ctinspection/ct-modal')
export const updateCtModal = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-modal')(data)
export const deleteCtModal = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-modal/delete')(data)

export const getCtModalUnit = targetInfo =>
  get(targetInfo, 'ctinspection/ct-modal-unit')
export const updateCtModalUnit = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-modal-unit')(data)
export const deleteCtModalUnit = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-modal-unit/delete')(data)

export const getCtInspctionType = targetInfo =>
  get(targetInfo, 'ctinspection/ct-inspection-type')
export const updateCtInspctionType = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-type')(data)
export const deleteCtInspctionType = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-type/delete')(data)

export const getCtInspctionInterval = targetInfo =>
  get(targetInfo, 'ctinspection/ct-inspection-interval')
export const updateCtInspctionInterval = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-interval')(data)
export const deleteCtInspctionInterval = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-interval/delete')(data)

export const getCtInspctionComments = targetInfo =>
  get(targetInfo, 'ctinspection/ct-inspection-comments')
export const updateCtInspctionComments = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-comments')(data)
export const deleteCtInspctionComments = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-comments/delete')(data)

export const getCtInspctionOverride = targetInfo =>
  get(targetInfo, 'ctinspection/ct-inspection-override')
export const updateCtInspctionOverride = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-override')(data)
export const deleteCtInspctionOverride = targetInfo => data =>
  post(targetInfo, 'ctinspection/ct-inspection-override/delete')(data)

export const getBidOffer = targetInfo => ({ startdate, enddate, unitid }) =>
  post(
    targetInfo,
    `pjm/bid-offer?${querystring.stringify({
      startdate: startdate.format('YYYY-MM-DD'),
      enddate: enddate.format('YYYY-MM-DD'),
    })}`
  )(unitid)

export const getPjmAdminBidOffer = targetInfo => ({
  startdate,
  enddate,
  unitid,
}) =>
  post(
    targetInfo,
    `pjm-admin/bid-offer?${querystring.stringify({
      startdate: startdate.format('YYYY-MM-DD'),
      enddate: enddate.format('YYYY-MM-DD'),
    })}`
  )(unitid)

export const updateBidOffer = targetInfo => data =>
  post(targetInfo, 'pjm-admin/bid-offer/update')(data)
export const deleteBidOffer = targetInfo => data =>
  post(targetInfo, 'pjm-admin/bid-offer/delete')(data)
export const updateScheduleDetail = targetInfo => data =>
  post(targetInfo, 'pjm-admin/scheduler-offer/update')(data)
export const deleteScheduleDetail = targetInfo => data =>
  post(targetInfo, 'pjm-admin/scheduler-offer/delete')(data)
export const importBidOfferTest = targetInfo =>
  post(targetInfo, 'pjm-admin/import-bidoffer')()
export const importBidOfferAutoImport = targetInfo => date =>
  post(
    targetInfo,
    `pjm-admin/import-bidoffer-auto-import?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )()

export const importScheduleDetailsAutoImport = targetInfo => date =>
  post(
    targetInfo,
    `pjm-admin/import-schedule-detail-auto-import?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )()

export const getScheduleDetails = targetInfo => ({
  startdate,
  enddate,
  unitid,
}) =>
  post(
    targetInfo,
    `pjm/scheduler-offer?${querystring.stringify({
      startdate: startdate.format('YYYY-MM-DD'),
      enddate: enddate.format('YYYY-MM-DD'),
    })}`
  )(unitid)

export const importScheduleDetailsTest = targetInfo =>
  post(targetInfo, 'pjm-admin/import-schedule-detail-manual')()

export const importRTLmpTest = targetInfo =>
  post(targetInfo, 'pjm-admin/import-rt-lmp-manual')()

export const getOpThreshold = targetInfo => ({ startdate, enddate, unitid }) =>
  post(
    targetInfo,
    `pjm/op-threshold?${querystring.stringify({
      startdate: startdate.format('YYYY-MM-DD'),
      enddate: enddate.format('YYYY-MM-DD'),
    })}`
  )(unitid)

export const saveOpThreshold = targetInfo => data =>
  post(targetInfo, 'pjm/op-threshold/save')(data)

export const deleteOpThreshold = targetInfo => data =>
  post(targetInfo, 'pjm/op-threshold/delete')(data)

export const getOutageAnalyzerData = targetInfo => reqObj =>
  post(targetInfo, 'pjm/outage-analyzer')(reqObj)

export const downloadOutageAnalyzerReport = targetInfo => reqObj =>
  post(targetInfo, 'pjm/outage-analyzer-report')(reqObj)

export const getNetCapacitiveVerification = targetInfo => ({
  unit,
  year,
  season,
}) =>
  post(
    targetInfo,
    `pjm/net-cap-verification?${querystring.stringify({ year, season })}`
  )(unit)

export const netCapacitiveVerificationUpdate = targetInfo => data =>
  post(targetInfo, `pjm/net-cap-verification/update`)(data)

export const netCapacitiveVerificationDelete = targetInfo => data =>
  post(targetInfo, `pjm/net-cap-verification/delete`)(data)

export const verificationHourlyMWH = targetInfo => ({ start, end, unit }) =>
  get(
    targetInfo,
    `pjm/hourlyVerification?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
      unit,
    })}`
  )

export const getInstalledCapacitiveClaimed = targetInfo => unitId =>
  post(targetInfo, 'pjm-admin/installed-cap-claimed')(unitId)

export const saveInstalledCapacitiveClaimed = targetInfo => data =>
  post(targetInfo, 'pjm-admin/installed-cap-claimed/save')(data)

export const deleteInstalledCapacitiveClaimed = targetInfo => data =>
  post(targetInfo, 'pjm-admin/installed-cap-claimed/delete')(data)

export const getInstalledCapacitiveClaimedEffective = targetInfo => unitIdList =>
  post(targetInfo, 'pjm-admin/installed-cap-claimed-effective')(unitIdList)

export const netCapVerificationDataCheckExport = targetInfo => ({
  unitIds,
  startYear,
  endYear,
}) =>
  post(
    targetInfo,
    `pjm/net-cap-verification-check-export?${querystring.stringify({
      startYear,
      endYear,
    })}`
  )(unitIds)

export const netCapVerificationDataExport = targetInfo => ({
  unitIds,
  startYear,
  endYear,
}) =>
  post(
    targetInfo,
    `pjm/net-cap-verification-data-export?${querystring.stringify({
      startYear,
      endYear,
    })}`
  )(unitIds)

export const getPJMUnitExtension = targetInfo => ({ unitid }) =>
  post(targetInfo, `pjm-admin/pjm-unit`)(unitid)

export const savePJMUnitExtension = targetInfo => data =>
  post(targetInfo, 'pjm-admin/pjm-unit/save')(data)

export const deletePJMUnitExtension = targetInfo => data =>
  post(targetInfo, 'pjm-admin/pjm-unit/delete')(data)

export const getVerificationPnode = targetInfo =>
  get(targetInfo, `pjm-admin/verification-pnode`)

export const saveVerificationPnode = targetInfo => data =>
  post(targetInfo, 'pjm-admin/verification-pnode/save')(data)

export const deleteVerificationPnode = targetInfo => data =>
  post(targetInfo, 'pjm-admin/verification-pnode/delete')(data)

export const getCausecodeAlertlist = targetInfo =>
  get(targetInfo, `pjm-admin/causecode-alertlist`)

export const saveCausecodeAlertlist = targetInfo => data =>
  post(targetInfo, 'pjm-admin/causecode-alertlist/save')(data)

export const deleteCausecodeAlertlist = targetInfo => data =>
  post(targetInfo, 'pjm-admin/causecode-alertlist/delete')(data)

export const getPJMUnitTransfer = targetInfo => ({ unitid }) =>
  post(targetInfo, `pjm-admin/pjm-unit-transfer`)(unitid)

export const savePJMUnitTransfer = targetInfo => data =>
  post(targetInfo, 'pjm-admin/pjm-unit-transfer/save')(data)

export const deletePJMUnitTransfer = targetInfo => data =>
  post(targetInfo, 'pjm-admin/pjm-unit-transfer/delete')(data)

export const getVerificationLock = targetInfo =>
  get(targetInfo, `pjm-admin/verification-lock`)

export const saveVerificationLock = targetInfo => data =>
  post(targetInfo, 'pjm-admin/verification-lock/save')(data)

export const deleteVerificationLock = targetInfo => data =>
  post(targetInfo, 'pjm-admin/verification-lock/delete')(data)

export const getGuideMap = targetInfo => get(targetInfo, 'utils/guide-map')

export const getVerificationHourlyMwAdmin = targetInfo => ({ start, end }) =>
  get(
    targetInfo,
    `pjm-admin/verification-hourly-mw-admin?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    })}`
  )

export const importVerificationHourlyMwAdmin = targetInfo => ({ start, end }) =>
  get(
    targetInfo,
    `pjm-admin/import-verification-hourly-manual?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    })}`
  )

export const processVerificationHourlyMwAdmin = targetInfo => ({
  start,
  end,
}) =>
  get(
    targetInfo,
    `pjm-admin/process-verification-hourly-manual?${querystring.stringify({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    })}`
  )

export const getPjmOaHoliday = targetInfo => get(targetInfo, 'admin/oa-holiday')
export const savePjmOaHoliday = targetInfo => data =>
  post(targetInfo, 'admin/oa-holiday')(data)
export const deletePjmOaHoliday = targetInfo => data =>
  post(targetInfo, 'admin/oa-holiday/delete')(data)

export const retrievePjmRtImportData = targetInfo => data =>
  get(targetInfo, `pjm-admin/realtimelmp?${querystring.stringify(data)}`)

export const importPjmRtImportData = targetInfo => data =>
  get(
    targetInfo,
    `pjm-admin/pjm-rt-lmp-autoimport?${querystring.stringify(data)}`
  )

export const pjmRtLmpInsert = targetInfo => data =>
  post(targetInfo, 'pjm-admin/insert-rt-lmp')(data)

export const pjmRtLmpdelete = targetInfo => data =>
  post(targetInfo, 'pjm-admin/delete-rt-lmp')(data)

export const pjmEventPending = targetInfo => ({ year, month, unitIds }) =>
  post(
    targetInfo,
    `pjm/event-pending?${querystring.stringify({ year, month })}`
  )(unitIds)

export const pjmEventPendingApprove = targetInfo => data =>
  post(targetInfo, `pjm/event-pending-approval`)(data)

export const peakDefinition = targetInfo =>
  get(targetInfo, 'pjm-admin/peak-definition')

export const peakDefinitionUpdate = targetInfo => data =>
  post(targetInfo, 'pjm-admin/peak-definition')(data)

export const peakDefinitionDelete = targetInfo => data =>
  post(targetInfo, 'pjm-admin/peak-definition/delete')(data)

export const getDailyViewDetails = targetInfo => data =>
  post(targetInfo, 'monthlyview/dailyview')(data)
export const getDailyUnitViewDetails = targetInfo => data =>
  post(targetInfo, 'monthlyview/dailyunitview')(data)

/* ocid support */
export const convertIdToken = targetInfo => jwt =>
  get({ ...targetInfo, jwt }, 'auth/token/convert-id-token')

export const getApsPiEntity = targetInfo => get(targetInfo, 'aps/pi-entity')
export const updateApsPiEntity = targetInfo => data =>
  post(targetInfo, 'aps/pi-entity')(data)
export const deleteApsPiEntity = targetInfo => data =>
  post(targetInfo, 'aps/pi-entity/delete')(data)

export const deletWebserviceLog = targetInfo =>
  post(targetInfo, 'common/webservicelog/delete')()

export const pjmGadsAcctManager = targetInfo => ({ startDate, endDate }) =>
  get(
    targetInfo,
    `pjm-admin/gads-acctmanager?${querystring.stringify({
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    })}`
  )

export const getKindleDataImportLog = targetInfo => date =>
  get(
    targetInfo,
    `kindle-datatransfer/data-import-log?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
    })}`
  )

export const deletKindleDataImportLog = targetInfo =>
  post(targetInfo, 'kindle-datatransfer/remove-data-import-log')()

export const getKindleLogBlob = targetInfo => ({ transId, type }) =>
  get(
    targetInfo,
    `kindle-datatransfer/data-import-log-blob?${querystring.stringify({
      transId,
      type,
    })}`
  )
export const getDbVersion = targetInfo => get(targetInfo, 'admin/dbversion')

export const getErrorTraceLogList = targetInfo => date =>
  get(
    targetInfo,
    `common/errortrace-log-list?${querystring.stringify({
      date: date.format('YYYY-MM-DD'),
      end: date
        .clone()
        .add(1, 'M')
        .format('YYYY-MM-DD'),
    })}`
  )

export const getErrorTraceLogblob = targetInfo => ({ id }) =>
  get(
    targetInfo,
    `common/errortrace-log/blob?${querystring.stringify({
      id,
    })}`
  )

export const deleteErrorLogTrace = targetInfo =>
  post(targetInfo, 'common/delete-errortrace-log')()

export const getAPSAdMappingData = targetInfo =>
  get(targetInfo, 'aps/ad-mapping')
export const saveAPSAdMappingUpdate = targetInfo => data =>
  post(targetInfo, 'aps/ad-mapping')(data)
export const deleteAPSAdMappingDelete = targetInfo => data =>
  post(targetInfo, 'aps/ad-mapping/delete')(data)
export const getMisoVerificationSeason = targetInfo =>
  get(targetInfo, 'miso/verification-season')

export const updateMisoVerificationSeason = targetInfo => data =>
  post(targetInfo, 'miso/verification-season')(data)

export const deleteMisoVerificationSeason = targetInfo => data =>
  post(targetInfo, 'miso/verification-season/delete')(data)

export const getMisoVerificationTestDefault = targetInfo =>
  get(targetInfo, 'miso/miso-test-default')

export const updateMisoVerificationTestDefault = targetInfo => data =>
  post(targetInfo, 'miso/miso-test-default')(data)

export const deleteMisoVerificationTestDefault = targetInfo => data =>
  post(targetInfo, 'miso/miso-test-default/delete')(data)

export const getMisoMinTestDuration = targetInfo =>
  get(targetInfo, 'miso/miso-min-test-duration')

export const updateMisoMinTestDuration = targetInfo => data =>
  post(targetInfo, 'miso/miso-min-test-duration')(data)

export const deleteMisoMinTestDuration = targetInfo => data =>
  post(targetInfo, 'miso/miso-min-test-duration/delete')(data)

export const getMisoUser = targetInfo => get(targetInfo, 'miso/miso-user')

export const getMisoUserMapping = targetInfo =>
  get(targetInfo, 'miso/miso-user-mapping')

export const getMisoVerificationTest = targetInfo => ({
  season,
  year,
  unitId,
}) =>
  post(
    targetInfo,
    `miso/verification?${querystring.stringify({
      season,
      year,
    })}`
  )(unitId)

export const getMisoVerificationExtendedTest = targetInfo => ({
  season,
  year,
  unitId,
}) =>
  post(
    targetInfo,
    `miso/verification-ext?${querystring.stringify({
      season,
      year,
    })}`
  )(unitId)

export const getMisoVerificationTestData = targetInfo => ({
  season,
  year,
  unitId,
}) =>
  post(
    targetInfo,
    `miso/verification-data?${querystring.stringify({
      season,
      year,
    })}`
  )(unitId)

export const saveMisoVerificationTestata = targetInfo => data =>
  post(targetInfo, `miso/verification/save`)(data)

export const deleteMisoVerificationTestata = targetInfo => data =>
  post(targetInfo, `miso/verification/delete`)(data)

export const misoNetCapVerificationDataCheckExport = targetInfo => ({
  unitIds,
  startYear,
  endYear,
}) =>
  post(
    targetInfo,
    `miso/net-cap-verification-check-export?${querystring.stringify({
      startYear,
      endYear,
    })}`
  )(unitIds)

export const misoNetCapVerificationDataExport = targetInfo => ({
  unitIds,
  startYear,
  endYear,
}) =>
  post(
    targetInfo,
    `miso/net-cap-verification-data-export?${querystring.stringify({
      startYear,
      endYear,
    })}`
  )(unitIds)
export const wind2PlantSelection = targetInfo => ({ year, month }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/get-plant-selection?${querystring.stringify({
      year,
      month,
    })}`
  )

// export const wind2PlantWithSubGroup = targetInfo =>
//   get(targetInfo, 'wind-nerc-powerdata/wind2-plant-with-subgroup')

export const wind2PiTagSelection = targetInfo => ({ plantId, frequency }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/pi-tag-selection?${querystring.stringify({
      plantId,
      frequency,
    })}`
  )
export const wind2PowerData = targetInfo => ({ year, month, plantid }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/get-data?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )
export const wind2Level2Validation = targetInfo => ({ plantid, year, month }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/wind2-level2-validationcall?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )

export const wind2AllPlant = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-get-all-plant`)

export const wind2AllPlantSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/plants`)(data)
export const wind2AllPlantdelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/plants/delete`)(data)

export const wind2AllSubgroup = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-get-all-subgroup`)

export const wind2AllSubgroupSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-subgroup`)(data)

export const wind2AllSubgroupDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-subgroup/delete`)(data)

export const getWind2TemplateGroups = targetInfo => ({ type }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/wind2-groups?${querystring.stringify({
      type,
    })}`
  )

export const saveWind2TemplateGroup = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-group/save')(data)

export const deleteWind2TemplateGroup = targetInfo => ({ group, type }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/wind2-group/delete?${querystring.stringify({
      group,
      type,
    })}`
  )

export const wind2AllContribCond = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-get-all-contrib-cond`)

export const wind2AllContribCondSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-get-all-contrib-cond`)(data)

export const wind2AllContribCondDelete = targetInfo => data =>
  post(
    targetInfo,
    `wind-nerc-powerdata/wind2-get-all-contrib-cond/delete`
  )(data)

export const wind2ScadaManf = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-scada-manf`)

export const wind2ScadaManfSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-scada-manf`)(data)

export const wind2ScadaManfDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-scada-manf/delete`)(data)

export const wind2turbineManf = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-turbine-manf`)

export const wind2TurbineSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-turbine-manf`)(data)

export const wind2TurbineDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-turbine-manf/delete`)(data)

export const wind2TurbineType = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-turbine-type`)

export const wind2TurbineTypeSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-turbine-type`)(data)

export const wind2TurbineTypeDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-turbine-type/delete`)(data)

export const wind2StorageManf = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-storage-manf`)

export const wind2StorageManfSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-storage-manf`)(data)

export const wind2StorageManfDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-storage-manf/delete`)(data)

export const wind2InvStorageManf = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-inv-storage-manf`)

export const wind2InvStorageManfSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-inv-storage-manf`)(data)

export const wind2InvStorageManfDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-inv-storage-manf/delete`)(data)

export const getWind2NercRegion = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-region`)
export const wind2NercRegionUpdate = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-region`)(data)
export const wind2NercRegionDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-region/delete`)(data)

export const wind2SubgroupFormation = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/wind2-subgroup-formation`)

export const wind2SubgroupFormationSave = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-subgroup-formation`)(data)

export const wind2SubgroupFormationDelete = targetInfo => data =>
  post(targetInfo, `wind-nerc-powerdata/wind2-subgroup-formation/delete`)(data)

export const getWind2Regime = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-regime')

export const getWind2OwnershipStatus = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-ownership-status')
export const getWind2Scada = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-scada')
export const getWind2TurbineManufacture = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-turbinemanufacture')
export const getWind2Turbulence = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-turbulence')
export const getWind2Shear = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-shear')
export const getWind2AvaliableStatus = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-avaliabilityStatus')
export const getWind2CountryList = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-country')
export const getWind2CountryState = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-country-state')
export const getWind2CountryProvince = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-country-province')
export const getWind2timeZone = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-time-zone-list')
export const getWind2Entity = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-entity')
export const saveWind2Entity = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-entity')(data)
export const deleteWind2Entity = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-entity/delete')(data)
export const wind2PerformanceUpdate = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/update-wind2-performance')(data)
export const wind2EventUpdate = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/update-wind2-event')(data)
export const wind2EventDelete = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/delete-wind2-event')(data)
export const wind2EventUpdateTurbine = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/update-wind2-event-turbine')(data)
export const wind2EventDeleteTurbine = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/delete-wind2-event-turbine')(data)
export const getWind2ContribCondtition = targetInfo =>
  get(targetInfo, `wind-nerc-powerdata/contrib-condition`)
export const getWind2CauseCode = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-causecode')
export const updateWind2CauseCode = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-causecode')(data)
export const deleteWind2CauseCode = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-causecode/delete')(data)
export const getWindUSerPlantAccess = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind-user-plant-access')
export const updateWindUSerPlantAccess = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind-user-plant-access')(data)
export const deleteWindUSerPlantAccess = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind-user-plant-access/delete')(data)

export const renameWind2StorageGroup = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-storage-group/rename')(data)
export const getWind2History = targetInfo => ({ dataName }) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/wind2-history?${querystring.stringify({
      dataName,
    })}`
  )
export const renameWind2SubGroup = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-subgroup/rename')(data)
export const renameWind2Plant = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-plant/rename')(data)
export const renameWind2Entity = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-entity/rename')(data)

export const getWind2errors = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-errors')
export const updateWind2errors = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-errors')(data)
export const deleteWind2errors = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-errors/delete')(data)
export const getWind2Lockcheck = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-lockcheck')
export const updateWind2Lockcheck = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-lockcheck')(data)
export const deleteWind2Lockcheck = targetInfo => data =>
  post(targetInfo, 'wind-nerc-powerdata/wind2-lockcheck/delete')(data)
export const getWind2LockDays = targetInfo =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-lockdays')
export const updateWind2LockDays = targetInfo => days =>
  get(targetInfo, 'wind-nerc-powerdata/wind2-lockdays-update?days=' + days)
export const wind2ImpactAnalysis = targetInfo => ({
  year,
  month,
  plantid,
  mode = 'ANALYZE', // --in_mode can be 'ANALYZE' or 'EXECUTE'
  createorprocess, // CREATE  PROCESS
  recalcTurbineperf,
}) =>
  get(
    targetInfo,
    `wind-nerc-powerdata/impact-analysis?${querystring.stringify({
      year,
      month,
      plantid,
      mode,
      createorprocess,
      recalcTurbineperf,
    })}`
  )

export const getSolarDays = targetInfo => ({ startDate, endDate, plantId }) =>
  get(
    targetInfo,
    `solar/solar-days-get?${querystring.stringify({
      plantId,
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    })}`
  )

export const saveSolarDays = targetInfo => data =>
  post(targetInfo, 'solar/solar-days')(data)

export const deleteSolarDays = targetInfo => data =>
  post(targetInfo, 'solar/solar-days-delete')(data)

export const getSolarEntity = targetInfo =>
  get(targetInfo, 'solar/solar-entity')
export const saveSolarEntity = targetInfo => data =>
  post(targetInfo, 'solar/solar-entity')(data)
export const deleteSolarEntity = targetInfo => data =>
  post(targetInfo, 'solar/solar-entity/delete')(data)

export const solarPerformanceUpdate = targetInfo => data =>
  post(targetInfo, 'solar/update-solar-performance')(data)
export const solarEventUpdate = targetInfo => data =>
  post(targetInfo, 'solar/update-solar-event')(data)
export const solarEventDelete = targetInfo => data =>
  post(targetInfo, 'solar/delete-solar-event')(data)
export const solarEventUpdateInverter = targetInfo => data =>
  post(targetInfo, 'solar/update-solar-inverter')(data)
export const solarEventDeleteInverter = targetInfo => data =>
  post(targetInfo, 'solar/delete-solar-inverter')(data)
export const solarLevel2Validation = targetInfo => ({ plantid, year, month }) =>
  get(
    targetInfo,
    `solar/solar-level2-validationcall?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )
export const solarPlantSelection = targetInfo => ({ year, month }) =>
  get(
    targetInfo,
    `solar/get-plant-selection?${querystring.stringify({
      year,
      month,
    })}`
  )

export const solarPlantDetails = targetInfo =>
  get(targetInfo, 'solar/solar-plant-details')

// export const solarPlantWithInverterGroup = targetInfo =>
//   get(targetInfo, 'solar/solar-plant-with-inverter-group')

// export const solarPlantWithStorageGroup = targetInfo =>
//   get(targetInfo, 'solar/solar-plant-with-storage-group')

export const solarPiTagSelection = targetInfo => ({ plantId, frequency }) =>
  get(
    targetInfo,
    `solar/pi-tag-selection?${querystring.stringify({
      plantId,
      frequency,
    })}`
  )

export const getSolarPowerData = targetInfo => ({ year, month, plantid }) =>
  get(
    targetInfo,
    `solar/get-data?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )

export const getSolarPlant = targetInfo => get(targetInfo, 'solar/plants-all')
export const saveSolarPlant = targetInfo => data =>
  post(targetInfo, 'solar/plants/save')(data)
export const deleteSolarPlant = targetInfo => data =>
  post(targetInfo, 'solar/plants/delete')(data)
export const getSolartimeZone = targetInfo =>
  get(targetInfo, 'solar/solar-time-zone-list')
export const getSolarCountryList = targetInfo =>
  get(targetInfo, 'solar/solar-country')
export const getSolarStateList = targetInfo =>
  get(targetInfo, 'solar/solar-state-province')
export const getCountryProvinceList = targetInfo =>
  get(targetInfo, 'solar/solar-country-province')
export const getSolarRegion = targetInfo =>
  get(targetInfo, 'solar/solar-region')
export const getSolarRegim = targetInfo => get(targetInfo, 'solar/solar-regime')
export const getSolarOwnershipStatus = targetInfo =>
  get(targetInfo, 'solar/solar-ownership-status')
export const getSolarContribCondtition = targetInfo =>
  get(targetInfo, `solar/contrib-condition`)

export const getSolarInverterGroup = targetInfo =>
  get(targetInfo, 'solar/solar-inverter-group')
export const updateSolarInverterGroup = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-group')(data)
export const deleteSolarInverterGroup = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-group/delete')(data)

export const getSolarInverterManufacturer = targetInfo =>
  get(targetInfo, 'solar/solar-inverter-manufacturer')
export const updateSolarInverterManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-manufacturer')(data)
export const deleteSolarInverterManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-manufacturer/delete')(data)
export const getSolarStorageGroup = targetInfo =>
  get(targetInfo, 'solar/solar-storage-group')
export const updateSolarStorageGroup = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-group')(data)
export const deleteSolarStorageGroup = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-group/delete')(data)
export const renameSolarStorageGroup = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-group/rename')(data)
export const getSolarHistory = targetInfo => ({ dataName }) =>
  get(
    targetInfo,
    `solar/solar-history?${querystring.stringify({
      dataName,
    })}`
  )
export const renameSolarInverterGroup = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-group/rename')(data)
export const renameSolarPlant = targetInfo => data =>
  post(targetInfo, 'solar/solar-plant/rename')(data)
export const renameSolarEntity = targetInfo => data =>
  post(targetInfo, 'solar/solar-entity/rename')(data)

export const getSolarScadaManufacturer = targetInfo =>
  get(targetInfo, 'solar/solar-scada-manufacturer')
export const updateSolarScadaManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-scada-manufacturer')(data)
export const deleteSolarScadaManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-scada-manufacturer/delete')(data)
export const getSolarDCinputType = targetInfo =>
  get(targetInfo, 'solar/solar-dc-input-type')
export const getSolarPanelTrackingType = targetInfo =>
  get(targetInfo, 'solar/solar-panel-tracking-type')
export const getSolarAvailabilityStatus = targetInfo =>
  get(targetInfo, 'solar/solar-availability-status')
export const getSolarCauseCode = targetInfo =>
  get(targetInfo, 'solar/solar-causecode')
export const updateSolarCauseCode = targetInfo => data =>
  post(targetInfo, 'solar/solar-causecode')(data)
export const deleteSolarCauseCode = targetInfo => data =>
  post(targetInfo, 'solar/solar-causecode/delete')(data)
export const getSolarerrors = targetInfo =>
  get(targetInfo, 'solar/solar-errors')
export const updateSolarerrors = targetInfo => data =>
  post(targetInfo, 'solar/solar-errors')(data)
export const deleteSolarerrors = targetInfo => data =>
  post(targetInfo, 'solar/solar-errors/delete')(data)
export const getsolarLockcheck = targetInfo =>
  get(targetInfo, 'solar/solar-lockcheck')
export const updatesolarLockcheck = targetInfo => data =>
  post(targetInfo, 'solar/solar-lockcheck')(data)
export const deletesolarLockcheck = targetInfo => data =>
  post(targetInfo, 'solar/solar-lockcheck/delete')(data)
export const getSolarUSerPlantAccess = targetInfo =>
  get(targetInfo, 'solar/solar-user-plant-access')
export const updateSolarUSerPlantAccess = targetInfo => data =>
  post(targetInfo, 'solar/solar-user-plant-access')(data)
export const deleteSolarUSerPlantAccess = targetInfo => data =>
  post(targetInfo, 'solar/solar-user-plant-access/delete')(data)

export const getsolarLockDays = targetInfo =>
  get(targetInfo, 'solar/solar-lockdays')

export const updatesolarLockDays = targetInfo => days =>
  get(targetInfo, 'solar/solar-lockdays-update?days=' + days)

export const getSolarStorageInverterManufacturer = targetInfo =>
  get(targetInfo, 'solar/solar-storage-inv-manufacturer')
export const updateSolarStorageInverterManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-inv-manufacturer')(data)
export const deleteSolarStorageInverterManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-inv-manufacturer/delete')(data)
export const getSolarInverterGroupFormation = targetInfo =>
  get(targetInfo, 'solar/solar-inverter-formation')
export const updateSolarInverterGroupFormation = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-formation')(data)
export const deleteSolarInverterGroupFormation = targetInfo => data =>
  post(targetInfo, 'solar/solar-inverter-formation/delete')(data)

export const getSolarStorageManufacturer = targetInfo =>
  get(targetInfo, 'solar/solar-storage-manufacturer')
export const updateSolarStorageManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-manufacturer')(data)
export const deleteSolarStorageManufacturer = targetInfo => data =>
  post(targetInfo, 'solar/solar-storage-manufacturer/delete')(data)
export const getSolarInverterIdByPlantId = targetInfo => plantId =>
  get(targetInfo, 'solar/solar-inverterformation-by-plant?plant=' + plantId)

export const getSolarStorageType = targetInfo =>
  get(targetInfo, 'solar/solar-storage-type')

export const getSolarInvPerormanceWithInvEvents = targetInfo => ({
  year,
  month,
  plantid,
}) =>
  get(
    targetInfo,
    `solar/get-inverter-performance-data?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )

export const operationSolarInvPerormanceWithInvEvents = targetInfo => data =>
  post(targetInfo, 'solar/save-inverter-performance-data/save')(data)
export const operationSolarInvPerormanceWithInvEventsDelete = targetInfo => data =>
  post(targetInfo, 'solar/save-inverter-performance-data/delete')(data)
export const createSolarInvPerormanceWithInvEvents = targetInfo => ({
  year,
  month,
  plantid,
}) =>
  get(
    targetInfo,
    `solar/create-inverter-performance-data?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )

export const solarProcessCreateEventData = targetInfo => ({
  year,
  month,
  plantid,
}) =>
  get(
    targetInfo,
    `solar/process-create-nerc-events?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )

export const solarProcessPerformanceData = targetInfo => ({
  year,
  month,
  plantid,
}) =>
  get(
    targetInfo,
    `solar/process-performance-events?${querystring.stringify({
      year,
      month,
      plantid,
    })}`
  )

export const solarProcessEventAndPerformanceData = targetInfo => ({
  year,
  month,
  plantid,
  type = 'A',
}) =>
  get(
    targetInfo,
    `solar/process-nerc-and-performance-events?${querystring.stringify({
      year,
      month,
      plantid,
      type,
    })}`
  )
export const solarImpactAnalysis = targetInfo => ({
  year,
  month,
  plantid,
  mode = 'ANALYZE', // --in_mode can be 'ANALYZE' or 'EXECUTE'
  createorprocess,
  recalcInverterperf,
}) =>
  get(
    targetInfo,
    `solar/impact-analysis?${querystring.stringify({
      year,
      month,
      plantid,
      mode,
      createorprocess,
      recalcInverterperf,
    })}`
  )

export const getPITag = (targetInfo, isWind = false) =>
  get(targetInfo, 'admin/pi-tag?piType=' + (isWind ? 'WIND' : 'SOLAR'))
export const getWind2PI10MinData = targetInfo => ({
  startDate,
  endDate,
  piTags,
  plantId,
  allPiTagSelected,
}) =>
  post(
    targetInfo,
    `pi-row-data/wind2/10min-get?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      plantId,
      allPiTagSelected,
    })}`
  )(piTags)

export const saveWind2PI10MinData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/wind2/10min')(data)

export const deleteWind2PI10MinData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/wind2/10min-delete')(data)

export const getWind2PIHourlyData = targetInfo => ({
  startDate,
  endDate,
  piTags,
  plantId,
  allPiTagSelected,
}) =>
  post(
    targetInfo,
    `pi-row-data/wind2/hourly-get?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      plantId,
      allPiTagSelected,
    })}`
  )(piTags)

export const saveWind2PIHourlyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/wind2/hourly')(data)

export const deleteWind2PIHourlyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/wind2/hourly-delete')(data)

export const getWind2PIDailyData = targetInfo => ({
  startDate,
  endDate,
  piTags,
  plantId,
  allPiTagSelected,
}) =>
  post(
    targetInfo,
    `pi-row-data/wind2/daily-get?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      plantId,
      allPiTagSelected,
    })}`
  )(piTags)

export const saveWind2PIDailyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/wind2/daily')(data)

export const deleteWind2PIDailyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/wind2/daily-delete')(data)

export const getSolarPI10MinData = targetInfo => ({
  startDate,
  endDate,
  piTags,
  plantId,
  allPiTagSelected,
}) =>
  post(
    targetInfo,
    `pi-row-data/solar/10min-get?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      plantId,
      allPiTagSelected,
    })}`
  )(piTags)

export const saveSolarPI10MinData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/solar/10min')(data)

export const deleteSolarPI10MinData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/solar/10min-delete')(data)

export const getSolarPIHourlyData = targetInfo => ({
  startDate,
  endDate,
  piTags,
  plantId,
  allPiTagSelected,
}) =>
  post(
    targetInfo,
    `pi-row-data/solar/hourly-get?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      plantId,
      allPiTagSelected,
    })}`
  )(piTags)

export const saveSolarPIHourlyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/solar/hourly')(data)

export const deleteSolarPIHourlyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/solar/hourly-delete')(data)

export const getSolarPIDailyData = targetInfo => ({
  startDate,
  endDate,
  piTags,
  plantId,
  allPiTagSelected,
}) =>
  post(
    targetInfo,
    `pi-row-data/solar/daily-get?${querystring.stringify({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      plantId,
      allPiTagSelected,
    })}`
  )(piTags)

export const saveSolarPIDailyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/solar/daily')(data)

export const deleteSolarPIDailyData = targetInfo => data =>
  post(targetInfo, 'pi-row-data/solar/daily-delete')(data)

export const getPITagWithCriteria = (targetInfo, isWind = false) => ({
  plant,
  tagType,
}) =>
  get(
    targetInfo,
    `admin/pi-tag-with-criteria?${querystring.stringify({
      plants: plant,
      tagTypes: tagType,
      piType: isWind ? 'WIND' : 'SOLAR',
    })}`
  )

export const savePITag = targetInfo => data =>
  post(targetInfo, 'admin/pi-tag/save')(data)

export const deletePITag = targetInfo => data =>
  post(targetInfo, 'admin/pi-tag/delete')(data)

export const getAdminPiTagTypes = targetInfo =>
  get(targetInfo, 'admin/pi-tag/pi-tag-type')

export const getAdditionalEventTags = targetInfo =>
  get(targetInfo, 'admin/card97-ext-spec')

export const saveAdditionalEventTags = targetInfo => data =>
  post(targetInfo, 'admin/card97-ext-spec/save')(data)

export const getAdditionalMonthlyTags = targetInfo =>
  get(targetInfo, 'admin/card95-ext-spec')

export const saveAdditionalMonthlyTags = targetInfo => data =>
  post(targetInfo, 'admin/card95-ext-spec/save')(data)

export const getProcessAll = targetInfo => ({ fromDate, toDate }) =>
  get(
    targetInfo,
    `common/monthly-error-log?${querystring.stringify({
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD'),
    })}`
  )

export const saveProcessAll = targetInfo => ({ month, year }) =>
  get(
    targetInfo,
    `common/monthly-error-log/save?${querystring.stringify({
      year,
      month,
    })}`
  )

export const validateProcessAll = targetInfo => ({ month, year }) =>
  get(
    targetInfo,
    `common/monthly-error-log/validate?${querystring.stringify({
      year,
      month,
    })}`
  )

export const processNEISOFileCreation = targetInfo => ({
  startMonth,
  year,
  endMonth,
  unitId,
}) =>
  get(
    targetInfo,
    `common/neiso-file/create?${querystring.stringify({
      unitId,
      year,
      startMonth,
      endMonth,
    })}`
  )

export const exportNEISOFile = targetInfo => ({
  startMonth,
  year,
  endMonth,
  unitId,
}) =>
  post(
    targetInfo,
    `common/neiso-file/export?${querystring.stringify({
      unitId,
      year,
      startMonth,
      endMonth,
    })}`
  )()

export const processNYISOFileCreation = targetInfo => ({
  startMonth,
  year,
  endMonth,
  unitId,
}) =>
  get(
    targetInfo,
    `common/nyiso-file/create?${querystring.stringify({
      unitId,
      year,
      startMonth,
      endMonth,
    })}`
  )

export const exportNYISOFile = targetInfo => ({
  startMonth,
  year,
  endMonth,
  unitId,
}) =>
  post(
    targetInfo,
    `common/nyiso-file/export?${querystring.stringify({
      unitId,
      year,
      startMonth,
      endMonth,
    })}`
  )()

export const getUserMessageCategory = targetInfo =>
  get(targetInfo, `common/user-message-category`)

export const saveUserMessageCategory = targetInfo => data =>
  post(targetInfo, 'common/user-message-category/save')(data)

export const deleteUserMessageCategory = targetInfo => data =>
  post(targetInfo, 'common/user-message-category/delete')(data)

export const getMessageCategory = targetInfo =>
  get(targetInfo, `common/message-category`)

export const saveMessageCategory = targetInfo => data =>
  post(targetInfo, 'common/message-category/save')(data)

export const deleteMessageCategory = targetInfo => data =>
  post(targetInfo, 'common/message-category/delete')(data)

export const getUserMessage = targetInfo =>
  get(targetInfo, `common/user-message`)

export const saveUserMessage = targetInfo => data =>
  post(targetInfo, 'common/user-message/save')(data)

export const deleteUserMessage = targetInfo => data =>
  post(targetInfo, 'common/user-message/delete')(data)

export const getAddnMonthlyTracking = targetInfo => ({ year, month }) =>
  get(
    targetInfo,
    `common/card95-extension?${querystring.stringify({
      month,
      year,
    })}`
  )

export const saveAddnMonthlyTracking = targetInfo => data =>
  post(targetInfo, `common/card95-extension/save`)(data)

export const deleteAddnMonthlyTracking = targetInfo => data =>
  post(targetInfo, `common/card95-extension/delete`)(data)

export const getAdminMessage = targetInfo =>
  get(targetInfo, `common/user-message`)

export const postAdminMessageAck = targetInfo => data =>
  post(targetInfo, `common/user-message/ack`)(data)
export const getUnitPerfGroup = targetInfo =>
  get(targetInfo, `nrg-admin/unit-perf-group`)

export const saveUnitPerfGroup = targetInfo => ({ data, isNew }) =>
  post(
    targetInfo,
    `nrg-admin/unit-perf-group/save?${querystring.stringify({
      isNew,
    })}`
  )(data)

export const deleteUnitPerfGroup = targetInfo => ({ group }) =>
  post(
    targetInfo,
    `nrg-admin/unit-perf-group/delete?${querystring.stringify({
      group,
    })}`
  )()

export const getGroups = targetInfo => get(targetInfo, `nrg-admin/groups`)

export const getProfitCost = targetInfo =>
  get(targetInfo, `nrg-admin/profit-cost`)

export const saveProfitCost = targetInfo => data =>
  post(targetInfo, 'nrg-admin/profit-cost/save')(data)

export const deleteProfitCost = targetInfo => data =>
  post(targetInfo, 'nrg-admin/profit-cost/delete')(data)

export const getEventReportSchedulerSpec = targetInfo =>
  get(targetInfo, `nrg-admin/event-reportscheduler-spec`)

export const upsertEventReportSchedulerSpec = targetInfo => data =>
  post(targetInfo, 'nrg-admin/event-reportscheduler-spec')(data)

export const saveCard97ExtensionSpec = targetInfo => data =>
  post(targetInfo, 'nrg-admin/card97-ext-spec/save')(data)

export const getCard95ExtensionSpec = targetInfo =>
  get(targetInfo, 'nrg-admin/card95-ext-spec')

export const saveCard95ExtensionSpec = targetInfo => data =>
  post(targetInfo, 'nrg-admin/card95-ext-spec/save')(data)

export const getPeakDefinition = targetInfo =>
  get(targetInfo, 'nrg-admin/peak-definition')

export const savePeakDefinition = targetInfo => data =>
  post(targetInfo, 'nrg-admin/peak-definition/save')(data)

export const deletePeakDefinition = targetInfo => data =>
  post(targetInfo, 'nrg-admin/peak-definition/delete')(data)

/*     genon-tps screens     */
export const getTPSGenMapping = targetInfo => () =>
  get(targetInfo, 'genon/tps-gen-mapping')

export const saveTPSGenMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-gen-mapping/save')(data)

export const deleteTPSGenMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-gen-mapping/delete')(data)

export const getTPSLmpMapping = targetInfo => () =>
  get(targetInfo, 'genon/tps-lmp-mapping')

export const saveTPSLmpMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-lmp-mapping/save')(data)

export const deleteTPSLmpMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-lmp-mapping/delete')(data)

export const getTPSDAAwardMapping = targetInfo => () =>
  get(targetInfo, 'genon/tps-daaward-mapping')

export const saveTPSDAAwardMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-daaward-mapping/save')(data)

export const deleteTPSDAAwardMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-daaward-mapping/delete')(data)

export const getTPSMakeWholeMapping = targetInfo => () =>
  get(targetInfo, 'genon/tps-makewhole-mapping')

export const saveTPSMakeWholeMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-makewhole-mapping/save')(data)

export const deleteTPSMakeWholeMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-makewhole-mapping/delete')(data)

export const getTPSFuelPriceMapping = targetInfo => () =>
  get(targetInfo, 'genon/tps-fuelprice-mapping')

export const saveTPSFuelPriceMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-fuelprice-mapping/save')(data)

export const deleteTPSFuelPriceMapping = targetInfo => data =>
  post(targetInfo, 'genon/tps-fuelprice-mapping/delete')(data)

export const getTPSGenData = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `genon/tps-gen-data?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      location: units,
    })}`
  )

export const importTPSGenData = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `genon/tps-gen-data/import?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const getTPSLmpData = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `genon/tps-lmp-data?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      element: units,
    })}`
  )

export const importTPSLmpData = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `genon/tps-lmp-data/import?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const getTPSDAAwardData = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `genon/tps-daaward-data?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      location: units,
    })}`
  )

export const importTPSDAAwardData = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `genon/tps-daaward-data/import?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const getTPSMakeWholeData = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `genon/tps-makewhole-data?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      location: units,
    })}`
  )

export const importTPSMakeWholeData = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `genon/tps-makewhole-data/import?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const getTPSFuelPriceData = targetInfo => ({ start, end, units }) =>
  get(
    targetInfo,
    `genon/tps-fuelprice-data?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      location: units,
    })}`
  )

export const importTPSFuelPriceData = targetInfo => ({ start, end, units }) =>
  post(
    targetInfo,
    `genon/tps-fuelprice-data/import?${querystring.stringify({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      unitId: units,
    })}`
  )()

export const nrgCard95DataAnalyticsImport = targetInfo => year =>
  post(targetInfo, 'nrg-data-analytics/card95?year=' + year)()
export const nrgCard97DataAnalyticsImport = targetInfo => year =>
  post(targetInfo, 'nrg-data-analytics/card97?year=' + year)()

export const getTotpRegisterQrCode = targetInfo => request =>
  post(targetInfo, 'auth/2fa/totp/register-qr-code', {
    ignoreAuth: true,
  })(request)

export const getSolarField = targetInfo =>
  get(targetInfo, 'solar/get-plant-field-selection')

export const getAllFieldValue = targetInfo => ({ fieldName }) => {
  return get(
    targetInfo,
    `solar/get-field-value?${querystring.stringify({ fieldName })}`
  )
}

export const upsertFieldValue = targetInfo => data =>
  post(targetInfo, `solar/upsert-field-value`)(data)

export const deleteSolarFieldValue = targetInfo => data =>
  post(targetInfo, 'solar/field-value/delete')(data)

export const saveIIFOOutage = targetInfo => data =>
  post(targetInfo, 'nyiso/iifo-outage')(data)

export const updateIIFOOutage = targetInfo => data =>
  post(targetInfo, 'nyiso/iifo-outage/update')(data)

export const getIIFOOutage = targetInfo => data =>
  post(targetInfo, 'nyiso/iifo-outage/get')(data)

export const getAllIIFOOutage = targetInfo =>
  get(targetInfo, 'nyiso/iifo-outage/getall')

export const deleteIIFOOutage = targetInfo => data =>
  post(targetInfo, 'nyiso/iifo-outage/delete')(data)

export const savePLW = targetInfo => data => post(targetInfo, 'nyiso/plw')(data)

export const updatePLW = targetInfo => data =>
  post(targetInfo, 'nyiso/plw/update')(data)

export const getPLW = targetInfo => get(targetInfo, 'nyiso/plw/get')

export const deletePLW = targetInfo => data =>
  post(targetInfo, 'nyiso/plw/delete')(data)

export const getNyisoMarsReportParameter = targetInfo =>
  get(targetInfo, 'nyiso/mars-parameter')

export const upsertNyisoMarsReportParameter = targetInfo => data =>
  post(targetInfo, 'nyiso/mars-parameter')(data)

export const deleteNyisoMarsReportParameter = targetInfo => data =>
  post(targetInfo, 'nyiso/mars-parameter/delete')(data)
